/**
 * signals.js — Signal Intelligence Module (T0 + T1)
 * T0: Parse Engine + Data Model + Ticker Mapping + FX Normalization
 * T1: Live Price Integration + Proximity Gauges + Auto Status Transitions
 *
 * Exports:
 * - parseSignalHTML(html): Parse n8n HTML output into signal objects
 * - importSignals(html, sourceMeta): Full import flow — parse, normalize, store
 * - resolveTickerMap(name): Name → ticker resolution
 * - saveTickerMapping(): Save user-edited ticker map
 * - renderSig(): Render signals page
 * - openPasteSignal(): Open HTML paste modal
 * - execPasteSignal(): Execute parse + import from pasted HTML
 * - deleteSignal(id): Remove a signal
 * - expireSignal(id): Mark signal expired
 * - updateSignalStatuses(): Auto-transition signal statuses based on live prices
 * - setSignalFilter(key, value): Set filter state
 */

import { S, save, stampItem, tombstoneId, MONGO_SIG_CAP } from '../core/state.js';
import { esc, uid, now, f, fByCur, safeUrl, isoDate, promptSafe, normTicker } from '../core/utils.js';
import { toast, clMo } from '../ui/layout.js';
import { hVal, hPnl } from '../core/math.js';
import { chatJSON, isAIConfigured } from '../core/ai.js';
import { signalStance, actionDirection, ACTION_DIRECTION } from '../core/signal-stance.js';
import { uiConfirm } from '../ui/dialog.js';

// ============ T1: LIVE PRICE ENGINE ============

// Filter state (in-memory, not persisted)
let _sigFilter = { status: 'ALL', action: 'ALL', source: 'ALL', productGroup: 'ALL', lens: 'ALL', groupBy: 'none' };

/**
 * Get current price for a signal's ticker
 * Handles .IS suffix stripping for BIST tickers
 * Returns { price, changePct, stale, cur } or null
 */
function _getSignalPrice(sig) {
  if (!sig.ticker) return null;
  // Try exact match first
  let p = S.prices[sig.ticker];
  // Try without .IS suffix (BIST tickers stored without suffix in prices)
  if (!p && sig.ticker.endsWith('.IS')) {
    p = S.prices[normTicker(sig.ticker)];
  }
  if (!p) return null;
  // Mark stale if older than 2 hours
  const staleMs = 2 * 60 * 60 * 1000;
  return { price: p.price, changePct: p.changePct || 0, cur: p.cur || 'TRY', stale: Date.now() - (p.ts || 0) > staleMs };
}

/**
 * Convert price to TRY if needed (for comparison with supportLevelTRY/targetLevelTRY)
 */
function _priceTRY(priceObj) {
  if (!priceObj) return null;
  if (priceObj.cur === 'TRY') return priceObj.price;
  if (priceObj.cur === 'USD' && S.fxRate) return priceObj.price * S.fxRate;
  if (priceObj.cur === 'EUR' && S.fxRateEUR) return priceObj.price * S.fxRateEUR;
  return priceObj.price; // fallback — no conversion
}

/**
 * Calculate % distance from current price to a target level
 * Returns positive = above level, negative = below level
 */
function _pctDistance(current, level) {
  if (!current || !level || level === 0) return null;
  return ((current - level) / level) * 100;
}

/**
 * Auto-transition signal statuses based on live price data
 * Called after price refresh (from render loop or price engine callback)
 *
 * Rules:
 * - NEW → APPROACHING: price within 5% of support (for BUY) or target (for SELL)
 * - APPROACHING → TRIGGERED: price crosses support (BUY) or target (SELL)
 * - Only active signals (not EXPIRED, ACTED)
 */
export function updateSignalStatuses() {
  let changed = false;
  const APPROACH_PCT = 5; // within 5% = approaching
  const transitions = []; // collect transitions for notifications
  // Signals whose status moved on a NON-DIRECTIONAL call. They are deliberately kept out of
  // `transitions`, so they change state without raising a notification or an alert row.
  const flatOnly = new Set();

  (S.signals || []).forEach(sig => {
    if (sig.status === 'EXPIRED' || sig.status === 'ACTED') return;

    // Self-heal TRY levels: re-normalize each pass so a foreign level imported while FX was
    // unavailable (LevelTRY=null) resolves once FX loads — avoids stale/false comparisons. audit H-5.
    if (sig.supportLevel != null) sig.supportLevelTRY = _normalizeLevelToTRY(sig.supportLevel, sig.supportCcy, sig.ticker);
    if (sig.targetLevel != null) sig.targetLevelTRY = _normalizeLevelToTRY(sig.targetLevel, sig.targetCcy, sig.ticker);

    const priceObj = _getSignalPrice(sig);
    if (!priceObj) return;
    const currentTRY = _priceTRY(priceObj);
    if (!currentTRY) return;

    const prevStatus = sig.status;

    // ⚠ BY DIRECTION, NOT BY THE WORD "BUY". Measured 2026-08-16: **42 of the 111 signals that
    // carry a price level were never looked at here**, because anything that is not literally
    // `BUY` or `SELL` fell out of both branches and sat at `NEW` for ever. Routing through
    // `actionDirection` brings the three conditional buys — BUY_ON_CONDITION, BUY_ON_DIP,
    // BUY_WITH_STOP — into the support watch, which is exactly what "buy on dip" means.
    // ⚠ HOLD AND WAIT STAY UNWATCHED, DELIBERATELY, AND THAT IS 39 OF THE 42. Whether an
    // analyst's "wait for 330" should raise an alert is a design question, and the blast radius
    // was measured before answering it: switching those on today fires **9 alerts at once**, and
    // at least three of them are nonsense produced by a SEPARATE defect — a level with no
    // currency is stored as though it were TRY (SMH target 670 against a TRY price of 28,165).
    // Recorded in the report; it is not fixed by pretending the watcher is the problem.
    const _dir = actionDirection(sig.action);
    if (_dir === 'bull') {
      // Bullish call: watch the support level (price dropping toward support = the entry)
      const level = sig.supportLevelTRY;
      if (!level) return;
      const dist = _pctDistance(currentTRY, level);
      if (dist === null) return;

      if (dist <= 0 && sig.status !== 'TRIGGERED') {
        sig.status = 'TRIGGERED';
        sig._updatedAt = Date.now();
        changed = true;
      } else if (dist <= APPROACH_PCT && dist > 0 && sig.status === 'NEW') {
        sig.status = 'APPROACHING';
        sig._updatedAt = Date.now();
        changed = true;
      }
    } else if (_dir === 'bear') {
      // Bearish call: watch the target level (price rising toward target = the exit)
      const level = sig.targetLevelTRY;
      if (!level) return;
      const dist = _pctDistance(currentTRY, level);
      if (dist === null) return;

      if (dist >= 0 && sig.status !== 'TRIGGERED') {
        sig.status = 'TRIGGERED';
        sig._updatedAt = Date.now();
        changed = true;
      } else if (dist >= -APPROACH_PCT && dist < 0 && sig.status === 'NEW') {
        sig.status = 'APPROACHING';
        sig._updatedAt = Date.now();
        changed = true;
      }
    } else if (_dir === 'flat') {
      // ── A NON-DIRECTIONAL CALL IS WATCHED, BUT SILENTLY. His decision, 2026-08-16. ──
      //
      // 39 of his flat signals (HOLD / WAIT) name a price level and, until now, nothing ever
      // looked at any of them. An analyst saying "wait for 130" is not an entry or an exit, so it
      // is not worth interrupting him for — but when the price actually reaches the level he
      // should not have to find that out by reading dates. So the status moves and the Signals
      // page and the stance cards show it; `flatOnly` keeps it out of `transitions`, which is what
      // drives the browser notification and the `signalAlerts` row.
      //
      // ⚠ EITHER LEVEL, EACH FROM ITS OWN SIDE. A flat call carries no direction, so there is no
      // "the" level: a support is reached from above and a target from below, and whichever it
      // carries is the one watched. Measured before shipping: **3 distinct signals cross on the
      // first scan** — TUPRS target 340 against 358, THYAO 305 against 306, PGSUS 130 against 152
      // — all three on positions he holds, and all three sane now that `6c02ff7` fixed the level
      // currencies. Before that fix the same measurement produced nonsense forty-five times out.
      //
      // ⚠ NO `APPROACHING` FOR A FLAT CALL. Nought of the ten checks were within the 5% band, and
      // a near-miss on a level nobody asked him to act on is noise even when it is silent.
      const sup = sig.supportLevelTRY, tgt = sig.targetLevelTRY;
      const supHit = sup ? _pctDistance(currentTRY, sup) <= 0 : false;
      const tgtHit = tgt ? _pctDistance(currentTRY, tgt) >= 0 : false;
      if ((supHit || tgtHit) && sig.status !== 'TRIGGERED') {
        sig.status = 'TRIGGERED';
        sig._updatedAt = Date.now();
        changed = true;
        flatOnly.add(sig.id);
      }
    }

    // Forward-observed outcome (immutable): once a signal has triggered, freeze tp/sl on the
    // FIRST cross so confidence is path-accurate and stationary, not a live snapshot. audit H-6.
    if (!sig._outcome && (sig.status === 'TRIGGERED' || sig.status === 'ACTED')) {
      const o = _resolveSignalOutcome(sig);
      if (o) { sig._outcome = o; changed = true; }
    }

    // Track transition for notification. ⚠ A flat call is excluded on purpose — see the
    // `_dir === 'flat'` branch. It is the ONE thing separating "watched" from "interrupts him".
    if (sig.status !== prevStatus && !flatOnly.has(sig.id)) {
      transitions.push({ sig, from: prevStatus, to: sig.status, price: priceObj.price, cur: priceObj.cur });
    }
  });

  // Fire notifications for transitions
  if (transitions.length) {
    transitions.forEach(({ sig, from: _from, to, price, cur }) => {
      // Record alert in signalAlerts
      if (!S.signalAlerts) S.signalAlerts = [];
      const alertId = uid();
      S.signalAlerts.push({
        id: alertId,
        signalId: sig.id,
        type: to,
        threshold: to === 'TRIGGERED' ? (actionDirection(sig.action) === 'bull' ? sig.supportLevelTRY : sig.targetLevelTRY) : null,
        triggered: true,
        triggeredAt: now(),
        dismissedAt: null,
        notifyBrowser: true,
        notifySound: false,
        _updatedAt: Date.now()
      });

      // Browser notification (swNotify handles toast internally — no separate toast needed)
      _sendBrowserNotification(sig, to, price, cur);
    });
  }

  if (changed) save();
  return changed;
}

/**
 * Send browser notification for signal status change
 * Now delegates to SW-based push notification system for better background support
 */
function _sendBrowserNotification(sig, status, price, cur) {
  const title = status === 'TRIGGERED'
    ? `Signal TRIGGERED: ${sig.assetName}`
    : `Signal APPROACHING: ${sig.assetName}`;

  // The level named here has to be the one the watcher actually crossed, or the notification
  // describes a different number than the one that fired it.
  const lvlWord = actionDirection(sig.action) === 'bull' ? 'support' : 'target';
  const body = `${sig.action} ${sig.ticker || sig.assetName} @ ${f(price, 2)} ${cur} — ` +
    (status === 'TRIGGERED'
      ? `Price crossed ${lvlWord} level`
      : `Price within 5% of ${lvlWord} level`);

  // Use SW-based push system if available
  if (typeof window.swNotify === 'function') {
    window.swNotify('signalAlert', title, body, {
      tag: `sig-${sig.id}-${status}`,
      dedupeKey: `sig-${sig.id}-${status}`,
      ticker: sig.ticker || sig.assetName,
      action: 'signals',
      requireInteraction: status === 'TRIGGERED'
    });
    return;
  }

  // Fallback: toast + direct Notification API
  const emoji = status === 'TRIGGERED' ? '🔴' : '🟡';
  toast(`${emoji} ${sig.assetName} (${sig.ticker || '?'}) → ${status} @ ${f(price, 2)} ${cur}`, status === 'TRIGGERED' ? 'a-r' : 'a-y');

  if (!('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;
  if (!(S.settings && S.settings.notif)) return;

  try {
    new Notification(title, { body, icon: '/icons/icon-192.png', tag: `sig-${sig.id}-${status}` });
  } catch (e) {
    console.warn('[SPEKTRX] Notification failed:', e);
  }
}

/**
 * Dismiss a signal alert (mark as read)
 */
export function dismissSignalAlert(alertId) {
  const alert = (S.signalAlerts || []).find(a => a.id === alertId);
  if (alert) {
    alert.dismissedAt = now();
    alert._updatedAt = Date.now();
    save();
    renderSig();
  }
}

/**
 * Dismiss ALL undismissed signal alerts
 */
export function dismissAllSignalAlerts() {
  const undismissed = (S.signalAlerts || []).filter(a => a.triggered && !a.dismissedAt);
  if (!undismissed.length) return;
  undismissed.forEach(a => {
    a.dismissedAt = now();
    a._updatedAt = Date.now();
  });
  save();
  renderSig();
  toast(`${undismissed.length} alert(s) dismissed`, 'a-g');
}

/**
 * Set filter value and re-render
 */
export function setSignalFilter(key, value) {
  if (key in _sigFilter) {
    _sigFilter[key] = value;
    renderSig();
  }
}

/**
 * The `change` handler behind the three filter selects.
 *
 * ⚠ THE FIELD TRAVELS IN THE MARKUP, NOT IN THE HANDLER. `setupEventDelegation`'s change arm
 * calls `actions[name](e)` with the event and nothing else, so a per-field handler would mean
 * three near-identical registrations. `data-field` names which of `_sigFilter`'s keys the select
 * owns, and `setSignalFilter` still refuses a key it does not have.
 */
export function signalFilterChange(e) {
  const el = e && e.target;
  if (!el) return;
  setSignalFilter(el.dataset.field, el.value);
}

/** Clear all three filters in one render, not three. */
export function clearSignalFilters() {
  let touched = false;
  ['status', 'action', 'source'].forEach(k => {
    if (_sigFilter[k] !== 'ALL') { _sigFilter[k] = 'ALL'; touched = true; }
  });
  if (touched) renderSig();
}

/**
 * Build proximity bar HTML
 * pct: distance percentage (0 = at level, negative = past level)
 * direction: 'support' or 'target'
 */
function _proximityBar(pct, direction) {
  if (pct === null) return '';
  const absPct = Math.abs(pct);
  // Clamp to 0-100 for visual
  const fillPct = Math.min(100, Math.max(0, 100 - absPct));
  // Color: green when close, amber mid, red when far
  let color = '#3fb950';
  if (absPct > 10) color = '#d29922';
  if (absPct > 20) color = '#f85149';
  // Flip colors for "past level" (pct < 0 for support means price below support)
  if (pct < 0 && direction === 'support') color = '#f85149'; // below support = danger for BUY
  if (pct > 0 && direction === 'target') color = '#3fb950'; // above target = good for SELL

  const sign = pct >= 0 ? '+' : '';
  return `<div style="display:flex;align-items:center;gap:6px;margin-top:4px">
    <div class="gls-track" style="flex:1;height:4px;border-radius:2px;overflow:hidden">
      <div style="width:${fillPct}%;height:100%;background:${color};border-radius:2px;transition:width 0.3s"></div>
    </div>
    <span style="font-size:10px;color:${color};font-weight:600;min-width:42px;text-align:right">${sign}${pct.toFixed(1)}%</span>
  </div>`;
}

// ============ PARSE ENGINE ============

/**
 * Extract currency and numeric level from natural language text
 * Handles: "175 TL", "1.80 euro", "$5", "4.20 euro", "175 lira", "0.37 dollar"
 * Returns { value: number, currency: 'TRY'|'EUR'|'USD'|null }
 */
function _extractLevel(text) {
  if (!text) return null;
  const t = text.toLowerCase();

  // Patterns: "175 tl", "175 lira", "40 lira"
  const tryMatch = t.match(/([\d.,]+)\s*(?:tl|lira|₺)/);
  if (tryMatch) return { value: parseFloat(tryMatch[1].replace(',', '.')), currency: 'TRY' };

  // Patterns: "1.80 euro", "4.20 euro", "€1.80"
  const eurMatch = t.match(/([\d.,]+)\s*(?:euro|eur|€)/) || t.match(/(?:€)\s*([\d.,]+)/);
  if (eurMatch) return { value: parseFloat(eurMatch[1].replace(',', '.')), currency: 'EUR' };

  // Patterns: "$5", "5 dollar", "0.37 dollar", "$0.37"
  const usdMatch = t.match(/([\d.,]+)\s*(?:dollar|usd|\$)/) || t.match(/(?:\$)\s*([\d.,]+)/);
  if (usdMatch) return { value: parseFloat(usdMatch[1].replace(',', '.')), currency: 'USD' };

  // Plain number fallback (no currency indicator)
  const numMatch = t.match(/([\d.,]+)/);
  if (numMatch) return { value: parseFloat(numMatch[1].replace(',', '.')), currency: null };

  return null;
}

/**
 * Extract support and target levels from watch triggers and notes
 * Searches for keywords: support, target, resistance, level, zone, bottom, top, channel
 */
function _extractLevels(watchTriggers, notes) {
  const all = [...(watchTriggers || []), ...(notes || [])];
  let support = null, target = null;

  for (const line of all) {
    const lower = line.toLowerCase();

    // Support detection
    if (!support && (lower.includes('support') || lower.includes('bottom') || lower.includes('critical'))) {
      support = _extractLevel(line);
    }

    // Target detection
    if (!target && (lower.includes('target') || lower.includes('top') || lower.includes('toward') || lower.includes('potential'))) {
      target = _extractLevel(line);
    }
  }

  return { support, target };
}

/**
 * Parse n8n HTML output into array of raw signal objects
 * DOM-based extraction from consistent CSS class structure
 */
export function parseSignalHTML(html) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Extract source metadata
  const channelEl = doc.querySelector('.channel-badge');
  const titleEl = doc.querySelector('.source-title a') || doc.querySelector('.source-title');
  const dateEl = doc.querySelector('.meta-tag');

  const sourceMeta = {
    analyst: channelEl ? channelEl.textContent.trim() : 'Unknown',
    title: titleEl ? titleEl.textContent.trim() : '',
    url: titleEl && titleEl.href ? titleEl.href : '',
    // Normalised at the door. Stored verbatim, this produced 'May 2, 2026' rows beside ISO
    // ones and no reader could sort the two together. If the source prints something with no
    // year, the raw string is kept rather than guessed at — it is then visibly unparseable
    // instead of silently misdated. (audit 2026-08-03)
    date: dateEl ? (isoDate(dateEl.textContent.replace('Published:', '').trim())
      || dateEl.textContent.replace('Published:', '').trim()) : now()
  };

  // Extract asset cards
  const cards = doc.querySelectorAll('.asset-card');
  const signals = [];

  cards.forEach(card => {
    const nameEl = card.querySelector('.asset-name');
    const classEl = card.querySelector('.asset-class');

    // Action items
    const actionItems = card.querySelectorAll('.action-item');
    let analystAction = null, recommendedAction = null, trend = null;

    actionItems.forEach(item => {
      const label = (item.querySelector('.action-label')?.textContent || '').trim().toLowerCase();
      const valueEl = item.querySelector('.action-value');
      const value = valueEl ? valueEl.textContent.trim() : '';

      if (label.includes('analyst')) analystAction = value;
      else if (label.includes('recommended')) recommendedAction = value;
      else if (label.includes('trend')) trend = value;
    });

    // Content blocks
    const blocks = card.querySelectorAll('.content-block');
    const watchTriggers = [];
    const notes = [];
    const evidencePhrases = [];

    blocks.forEach(block => {
      const title = (block.querySelector('.content-block-title')?.textContent || '').trim().toLowerCase();
      const items = block.querySelectorAll('.list-item');
      const evidenceItems = block.querySelectorAll('.evidence-item');

      if (title.includes('watch') || title.includes('trigger')) {
        items.forEach(el => watchTriggers.push(el.textContent.trim()));
      } else if (title.includes('note')) {
        items.forEach(el => notes.push(el.textContent.trim()));
      } else if (title.includes('evidence')) {
        evidenceItems.forEach(el => {
          const phrase = el.querySelector('.evidence-phrase');
          if (phrase) evidencePhrases.push(phrase.textContent.trim());
        });
      }
    });

    // Extract levels from text
    const { support, target } = _extractLevels(watchTriggers, notes);

    signals.push({
      assetName: nameEl ? nameEl.textContent.trim() : 'Unknown',
      assetClass: classEl ? classEl.textContent.trim() : 'EQUITY',
      action: analystAction || recommendedAction || 'WATCH',
      trend: trend || 'UNKNOWN',
      supportLevel: support?.value || null,
      supportCcy: support?.currency || null,
      targetLevel: target?.value || null,
      targetCcy: target?.currency || null,
      watchTriggers,
      notes,
      evidencePhrases
    });
  });

  return { sourceMeta, signals };
}


// ============ TICKER MAPPING ============

/**
 * Built-in BIST reference table — common company names → ticker symbols
 * Used as fallback when user's tickerMap and holdings don't have a match
 */
const _BIST_REF = {
  // BIST-30 + major BIST-100
  'akbank': 'AKBNK.IS', 'arcelik': 'ARCLK.IS', 'aselsan': 'ASELS.IS',
  'bim': 'BIMAS.IS', 'coca cola': 'CCOLA.IS', 'dogan holding': 'DOHOL.IS',
  'dogus otomotiv': 'DOAS.IS', 'emlak konut': 'EKGYO.IS', 'enerjisa': 'ENJSA.IS',
  'enka': 'ENKAI.IS', 'eregli': 'EREGL.IS', 'ford otomotiv': 'FROTO.IS',
  'garanti': 'GARAN.IS', 'garanti bbva': 'GARAN.IS', 'halkbank': 'HALKB.IS',
  'is bankasi': 'ISCTR.IS', 'isbank': 'ISCTR.IS', 'is yatirim': 'ISMEN.IS',
  'is yatirim menkul': 'ISMEN.IS', 'kardemir': 'KRDMD.IS', 'koc holding': 'KCHOL.IS',
  'koç holding': 'KCHOL.IS', 'koza altin': 'KOZAL.IS', 'koza madencilik': 'KOZAA.IS',
  'logo': 'LOGO.IS', 'logo yazilim': 'LOGO.IS', 'mavi': 'MAVI.IS', 'mavi giyim': 'MAVI.IS',
  'migros': 'MGROS.IS', 'otokar': 'OTKAR.IS', 'oyak yatirim': 'OYAKC.IS',
  'pegasus': 'PGSUS.IS', 'petkim': 'PETKM.IS', 'sabanci': 'SAHOL.IS',
  'sabanci holding': 'SAHOL.IS', 'sasa': 'SASA.IS', 'sise cam': 'SISE.IS',
  'sisecam': 'SISE.IS', 'sok': 'SOKM.IS', 'sok marketler': 'SOKM.IS',
  'tav': 'TAVHL.IS', 'tekfen': 'TKFEN.IS', 'thy': 'THYAO.IS',
  'turk hava yollari': 'THYAO.IS', 'turkish airlines': 'THYAO.IS',
  'turkcell': 'TCELL.IS', 'turk telekom': 'TTKOM.IS', 'turktelekom': 'TTKOM.IS',
  'tupras': 'TUPRS.IS', 'ulker': 'ULKER.IS', 'vakifbank': 'VAKBN.IS',
  'vestel': 'VESTL.IS', 'vestel elektronik': 'VESTL.IS', 'yapi kredi': 'YKBNK.IS',
  'yapikredi': 'YKBNK.IS', 'zorlu enerji': 'ZOREN.IS',
  // Additional BIST stocks commonly in analyst reports
  'aksa akrilik': 'AKSA.IS', 'aksa': 'AKSA.IS', 'alarko holding': 'ALARK.IS',
  'anadolu efes': 'AEFES.IS', 'aydem enerji': 'AYDEM.IS', 'bagfas': 'BAGFS.IS',
  'brisa': 'BRISA.IS', 'celebi': 'CLEBI.IS', 'cimsa': 'CIMSA.IS',
  'deva holding': 'DEVA.IS', 'dogan sirketler grubu': 'DOHOL.IS',
  'eczacibasi': 'ECILC.IS', 'gediz elektrik': 'GEDZA.IS',
  'gubre fabrikalari': 'GUBRF.IS', 'gubretaş': 'GUBRF.IS', 'gubretas': 'GUBRF.IS',
  'hektas': 'HEKTS.IS', 'is gyoa': 'ISGYO.IS', 'is gyo': 'ISGYO.IS',
  'isgyo': 'ISGYO.IS', 'is gyoa': 'ISGYO.IS',
  'kartonsan': 'KARTN.IS', 'kent gida': 'KENT.IS',
  'kordsa': 'KORDS.IS', 'maret': 'MARET.IS',
  'nuh cimento': 'NUHCM.IS', 'netaş': 'NETAS.IS', 'netas': 'NETAS.IS',
  'otokar': 'OTKAR.IS', 'park elektrik': 'PRKME.IS',
  'tofas': 'TOASO.IS', 'tofaş': 'TOASO.IS', 'turk traktor': 'TTRAK.IS',
  'yeo teknoloji': 'YEOTK.IS', 'yeotk': 'YEOTK.IS',
  'aygaz': 'AYGAZ.IS', 'biotrend': 'BIGCH.IS',
  'indeks bilgisayar': 'INDES.IS', 'indeks': 'INDES.IS',
  'kervansaray': 'KVNSR.IS', 'kontrolmatik': 'KONTR.IS',
  'smart gunes enerjisi': 'SMRTG.IS', 'smart gunes': 'SMRTG.IS',
  'tab gida': 'TABGD.IS', 'tab': 'TABGD.IS',
  // Commodities & Crypto (common analyst mentions)
  'altin': 'GC=F', 'gold': 'GC=F', 'gumus': 'SI=F', 'silver': 'SI=F',
  'petrol': 'CL=F', 'crude oil': 'CL=F', 'bakir': 'HG=F', 'copper': 'HG=F',
  'bitcoin': 'BTC', 'btc': 'BTC', 'ethereum': 'ETH', 'eth': 'ETH',
};

/**
 * Resolve asset name to ticker symbol — multi-layer lookup:
 * 1. User's custom tickerMap (exact + case-insensitive)
 * 2. Holdings — match against h.ticker stripped or h.name
 * 3. Built-in BIST reference table (fuzzy)
 * 4. Return null if unknown
 *
 * When a match is found in layers 2-3, it's auto-saved to tickerMap
 * so future lookups are instant.
 */
// Built-in global asset reference (crypto / metals / indices / commodities) for Mongo analyst-named assets
const _GLOBAL_REF = {
  'bitcoin': 'BTC', 'spot bitcoin': 'BTC', 'btc': 'BTC',
  'ethereum': 'ETH', 'eth': 'ETH',
  'xrp': 'XRP', 'ripple': 'XRP',
  'tether gold': 'XAUT', 'xaut': 'XAUT',
  'ounce gold': 'XAU', 'gram gold': 'XAU', 'gold': 'XAU', 'altın': 'XAU', 'altin': 'XAU',
  'silver': 'XAG', 'gümüş': 'XAG', 'gumus': 'XAG',
  'platinum': 'XPT', 'palladium': 'XPD',
  's&p 500': 'SPX', 'sp500': 'SPX', 's&p500': 'SPX',
  'nasdaq 100': 'NDX', 'nasdaq': 'NDX', 'nasdaq100': 'NDX',
  'dollar index': 'DXY', 'dxy': 'DXY', 'dolar endeksi': 'DXY',
  'brent oil': 'BRENT', 'brent': 'BRENT', 'oil': 'BRENT', 'crude oil': 'BRENT', 'ham petrol': 'BRENT',
  'istanbul stock exchange': 'XU100', 'bist 100': 'XU100', 'bist100': 'XU100', 'bist': 'XU100',
  'istanbul borsası': 'XU100', 'istanbul borsasi': 'XU100', 'borsa istanbul': 'XU100', 'bist 100 endeksi': 'XU100', 'bist endeksi': 'XU100',
};

// Turkish-aware fold for matching: lowercases, strips combining marks (İ→i̇→i),
// and folds dotless ı→i so analyst text with İ/ı/diacritics matches plain-ASCII keys.
const _foldKey = s => (s || '').toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/ı/g, 'i').trim();
const _BIST_REF_F = Object.fromEntries(Object.entries(_BIST_REF).map(([k, v]) => [_foldKey(k), v]));
const _GLOBAL_REF_F = Object.fromEntries(Object.entries(_GLOBAL_REF).map(([k, v]) => [_foldKey(k), v]));

/**
 * Derive a product-group bucket for a signal — DATA-DRIVEN, not a fixed list.
 * Known classes map to friendly buckets; BIST vs US split by .IS ticker suffix;
 * unknown asset classes pass through as their own bucket (so new categories from
 * the feed appear automatically instead of being forced into "Other").
 */
export function signalProductGroup(sig) {
  const cls = _foldKey(sig && sig.assetClass);
  const tk = (sig && sig.ticker) || '';
  const isIS = /\.is$/i.test(tk);
  if (!cls) return 'General';
  if (cls.includes('crypto') || cls.includes('coin')) return 'Crypto';
  if (cls.includes('fund') || cls.includes('fon')) return 'Fund';
  if (cls.includes('metal')) return 'Precious Metals';
  if (cls.includes('commodity') || cls.includes('emtia')) return 'Commodity';
  if (cls.includes('etf')) return isIS ? 'BIST ETF' : 'US ETF';
  // IMPORTANT: this feed labels BIST-listed equities as "EQUITY_INDEX" — these are individual
  // stocks (MAVI.IS, DOAS.IS…), NOT market indices. Match equity BEFORE the index rule.
  if (cls.includes('equity') || cls.includes('stock') || cls.includes('hisse') || cls.includes('share')) return (isIS || cls === 'equity_index') ? 'BIST Equity' : 'US Equity';
  if (cls.includes('index') || cls.includes('endeks')) return 'Index';
  if (cls === 'other' || cls.includes('general') || cls.includes('info') || cls.includes('macro') || cls.includes('genel')) return 'General';
  // data-driven fallback: unknown class becomes its own bucket (Title Case)
  const raw = String(sig.assetClass).trim();
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

/**
 * AI semantic fallback for ticker resolution — call ONLY when resolveTickerMap()
 * returns null. Handles Turkish/free-text names and index-vs-tradeable distinction.
 * Returns { ticker, assetClass, tradeable, confidence } or null. Never throws.
 * Caller should cache a confident result into S.tickerMap so it runs once.
 */
export async function resolveTickerAI(name) {
  if (!name || !isAIConfigured()) return null;
  try {
    const r = await chatJSON([
      { role: 'system', content: 'You map a financial asset name (may be Turkish) to its canonical trading symbol. BIST (Borsa Istanbul) equities use a .IS suffix (e.g. "Doğuş Otomotiv" -> DOAS.IS). Indices/exchanges are NOT directly tradeable (e.g. "İstanbul Borsası" -> XU100 with tradeable:false). Crypto -> bare symbol (BTC, ETH). Metals -> XAU/XAG. If unsure, return null ticker. Reply ONLY with JSON: {"ticker":"<symbol or null>","assetClass":"<equity|etf|crypto|commodity|fund|index|fx|other>","tradeable":<true|false>,"confidence":<0..1>}' },
      // The asset name comes from the feed, not from him. (2026-08-14)
      { role: 'user', content: `Asset name: "${promptSafe(name, 80)}"` }
    ], { maxTokens: 120, temperature: 0 });
    const d = r && r.data;
    if (!d || !d.ticker) return null;
    return { ticker: String(d.ticker), assetClass: d.assetClass || null, tradeable: d.tradeable !== false, confidence: typeof d.confidence === 'number' ? d.confidence : 0.6 };
  } catch (_e) { return null; }
}

/**
 * AI extraction of trade params from analyst free-text (watch_triggers + notes).
 * Handles conditional ("if drops to 64k buy") and range ("accumulate 60-64k") phrasing.
 * Unstated values stay null (never invented). Returns structured object or null.
 * Never throws. Caller caches into sig._aiExtracted (IDB, not a Supabase table).
 */
export async function extractSignalLevelsAI(sig) {
  if (!sig || !isAIConfigured()) return null;
  // Analyst prose out of the signals feed — text this app did not write. The extractor below
  // is asked for JSON only and its output is validated, but the input still must not be able
  // to close the message and open a new instruction. (2026-08-14)
  const text = [...(sig.watchTriggers || []), ...(sig.notes || [])]
    .map(t => promptSafe(t, 400)).filter(Boolean).join('\n').trim();
  if (!text) return null;
  try {
    const r = await chatJSON([
      { role: 'system', content: 'Extract trade parameters from an analyst note. Levels may be conditional ("if drops to 64k buy") or ranges ("accumulate 60-64k"). If a value is not stated, use null — do NOT invent it. Reply ONLY with JSON: {"direction":"buy|sell|neutral","entry":{"type":"price|range|conditional|none","low":<num|null>,"high":<num|null>},"tp":[<num>,...],"sl":<num|null>,"currency":"TRY|USD|EUR|null","confidence":<0..1>,"evidence":"<short quote>"}' },
      { role: 'user', content: `Asset: ${sig.assetName || ''}\nNotes:\n${text}` }
    ], { maxTokens: 300, temperature: 0 });
    return (r && r.data) || null;
  } catch (_e) { return null; }
}

/**
 * AI enrichment pass over signals — fills missing tickers (semantic fallback) and
 * extracts structured levels for signals lacking them. EXPLICIT trigger only (not on
 * render/hot import path). Cost-controlled: sequential, batch-capped, single save() at end.
 */
export async function enrichSignalsAI(opts = {}) {
  if (!isAIConfigured()) { toast('AI not configured — set it in Settings', 'a-r'); return; }
  const cap = opts.cap || 25; // max AI calls per run (cost guard)
  const active = (S.signals || []).filter(s => s.status !== 'EXPIRED');
  let tk = 0, ex = 0, calls = 0;
  for (const sig of active) {
    if (calls >= cap) break;
    // 1) semantic ticker fallback — only if still unmapped
    if (!sig.ticker && sig.assetName) {
      const ai = await resolveTickerAI(sig.assetName);
      calls++;
      if (ai && ai.ticker && ai.confidence >= 0.7) {
        S.tickerMap[sig.assetName] = ai.ticker; // cache once → future lookups instant
        sig.ticker = ai.ticker;
        sig.supportLevelTRY = _normalizeLevelToTRY(sig.supportLevel, sig.supportCcy, sig.ticker);
        sig.targetLevelTRY = _normalizeLevelToTRY(sig.targetLevel, sig.targetCcy, sig.ticker);
        sig._aiTicker = { tradeable: ai.tradeable, confidence: ai.confidence };
        sig._updatedAt = Date.now();
        tk++;
      }
    }
    if (calls >= cap) break;
    // 2) structured level extraction — only once per signal
    if (!sig._aiExtracted && ((sig.watchTriggers && sig.watchTriggers.length) || (sig.notes && sig.notes.length))) {
      const d = await extractSignalLevelsAI(sig);
      calls++;
      if (d) { sig._aiExtracted = { ...d, extractedAt: now() }; sig._updatedAt = Date.now(); ex++; }
    }
  }
  if (tk || ex) { stampItem(S); save(); } // single save at end — no tight-loop saves
  toast(`AI enrich: ${tk} ticker(s), ${ex} extraction(s)` + (calls >= cap ? ` (capped ${cap})` : ''), 'a-g');
  renderSig();
}

export function resolveTickerMap(name) {
  if (!name) return null;
  const lower = name.toLowerCase().trim();
  const folded = _foldKey(name);

  // Layer 1: User's custom tickerMap (exact + case-insensitive)
  if (S.tickerMap[name]) return S.tickerMap[name];
  for (const [key, val] of Object.entries(S.tickerMap)) {
    if (val && key.toLowerCase() === lower) return val;
  }

  // Layer 2: Holdings — match against ticker (stripped) or name
  for (const h of (S.holdings || [])) {
    const tkLower = normTicker(h.ticker).toLowerCase();
    const nmLower = (h.name || '').toLowerCase();
    if (tkLower === lower || nmLower === lower || nmLower.includes(lower) || lower.includes(tkLower)) {
      const fullTicker = h.ticker.includes('.') ? h.ticker : h.ticker + '.IS';
      // Auto-save to tickerMap for future
      S.tickerMap[name] = fullTicker;
      return fullTicker;
    }
  }

  // Layer 3: Built-in BIST reference table (Turkish-aware folded match)
  if (_BIST_REF_F[folded]) {
    S.tickerMap[name] = _BIST_REF_F[folded];
    return _BIST_REF_F[folded];
  }
  // Fuzzy: try partial match (e.g. "Dogus Otomotiv" → "dogus otomotiv")
  for (const [refName, refTicker] of Object.entries(_BIST_REF_F)) {
    if (refName.length >= 3 && (folded.includes(refName) || refName.includes(folded))) {
      S.tickerMap[name] = refTicker;
      return refTicker;
    }
  }

  // Layer 4: Built-in global reference (crypto/metals/indices/commodities)
  if (_GLOBAL_REF_F[folded]) {
    S.tickerMap[name] = _GLOBAL_REF_F[folded];
    return _GLOBAL_REF_F[folded];
  }

  return null;
}

/**
 * Save ticker mapping from UI form + retroactively update existing signals
 */
export function saveTickerMapping() {
  const rows = document.querySelectorAll('.tm-row');
  let _newMappings = 0;
  rows.forEach(row => {
    const nameEl = row.querySelector('.tm-name');
    const tickerEl = row.querySelector('.tm-ticker');
    if (nameEl && tickerEl) {
      const name = nameEl.textContent.trim();
      const ticker = tickerEl.value.trim();
      if (name && ticker) {
        if (!S.tickerMap[name] || S.tickerMap[name] !== ticker) _newMappings++;
        S.tickerMap[name] = ticker;
      }
    }
  });

  // Retroactively update existing signals that are missing tickers or have stale mappings
  let updated = 0;
  (S.signals || []).forEach(sig => {
    const resolved = resolveTickerMap(sig.assetName);
    if (resolved && resolved !== sig.ticker) {
      sig.ticker = resolved;
      // Re-normalize FX levels
      sig.supportLevelTRY = _normalizeLevelToTRY(sig.supportLevel, sig.supportCcy, sig.ticker);
      sig.targetLevelTRY = _normalizeLevelToTRY(sig.targetLevel, sig.targetCcy, sig.ticker);
      sig._updatedAt = Date.now();
      updated++;
    }
  });

  stampItem(S);
  save();

  let msg = 'Ticker mappings saved';
  if (updated) msg += ` — ${updated} signal(s) updated`;
  toast(msg, 'a-g');
  renderSig();
}


// ============ FX NORMALIZATION ============

/**
 * Normalize a level to TRY using live FX rates
 * Returns the TRY equivalent, or the original value if already TRY/no rate
 */
/**
 * The currency an instrument is quoted in, from the price feed. `''` when we have no price yet.
 *
 * This is the only honest answer to "what currency is this level in" when the analyst's own tag
 * is missing: a level quoted for an instrument is quoted in that instrument's currency.
 */
function _levelCcy(ticker) {
  if (!ticker) return '';
  const p = S.prices && (S.prices[ticker] || S.prices[normTicker(ticker)]);
  return (p && p.cur) || '';
}

/**
 * A price level in TRY, or null when it cannot be resolved yet.
 *
 * ⚠ AN UNTAGGED LEVEL IS NOT A TRY LEVEL, AND THE OLD FIRST LINE SAID IT WAS.
 * `if (!value || !currency) return value;` handed the number straight back whenever the analyst's
 * currency tag was missing — which is **120 of the 155 levels in his book**. For a BIST name that
 * is right by luck; for anything quoted in dollars it is a comparison against a TRY price that is
 * out by the FX rate. Measured 2026-08-16, three live instances:
 *
 *     SMH.IS   target 670   vs price $589.12 = 28,166 TRY    42x
 *     SMH.IS   target 548   vs price $589.12 = 28,166 TRY    51x
 *     EUR.IS   target  65   vs price €55.14  =  3,041 TRY    47x
 *
 * SMH's 670 is a real call — the ETF is at 589 — and the watcher could never have fired it.
 * ⚠ Note both tickers end in `.IS`: the ingest appends the BIST suffix to non-BIST names, so
 * "it looks like a BIST symbol" is not evidence of anything. `normTicker` already handles the
 * lookup; the currency has to come from the price, not from the shape of the ticker.
 *
 * ⚠ THE DOCTRINE WAS ALREADY WRITTEN THREE LINES BELOW THE BUG. The comment on the final `return
 * null` says a foreign level must NOT be compared as if it were TRY — and the first line did
 * exactly that for the untagged case. Rule #41.
 *
 * Falls back to the instrument's quote currency, then gives up. Giving up is safe because the
 * self-heal in `updateSignalStatuses` re-normalises on every pass, and because nothing can
 * compare a level for an instrument that has no price. (audit H-5, extended 2026-08-16)
 */
function _normalizeLevelToTRY(value, currency, ticker) {
  if (value == null || value === '') return value;
  const ccy = String(currency || '').trim() || _levelCcy(ticker);
  // No tag and no price: hand it back unchanged, exactly as before. Nothing can compare it —
  // every reader bails when `_getSignalPrice` is null — and the self-heal resolves it later.
  if (!ccy) return value;
  if (ccy === 'TRY') return value;

  if (ccy === 'USD' && S.fxRate) return value * S.fxRate;
  if (ccy === 'EUR' && S.fxRateEUR) return value * S.fxRateEUR;

  // Foreign level but FX rate unavailable — return null (unresolved) so it is NOT compared as
  // if it were TRY. Re-normalizes automatically once FX loads (updateSignalStatuses). audit H-5.
  return null;
}


// ============ IMPORT FLOW ============

/**
 * Full import: parse HTML → resolve tickers → normalize FX → store signals
 */
export function importSignals(html, _sourceOverrides) {
  const { sourceMeta, signals: rawSignals } = parseSignalHTML(html);

  if (!rawSignals.length) {
    toast('No signals found in HTML', 'a-r');
    return { imported: 0, unmapped: [] };
  }

  // Find or create source
  let source = S.signalSources.find(s => s.analyst === sourceMeta.analyst && s.type === 'html_paste');
  if (!source) {
    source = {
      id: uid(),
      name: sourceMeta.analyst,
      type: 'html_paste',
      analyst: sourceMeta.analyst,
      channel: sourceMeta.title,
      url: sourceMeta.url,
      trustScore: 50,
      totalSignals: 0,
      hitCount: 0,
      missCount: 0,
      assetClasses: [],
      lastFetchedAt: now(),
      _updatedAt: Date.now()
    };
    S.signalSources.push(source);
  }
  // Update source metadata on every import (channel/url may change per video)
  if (sourceMeta.title) source.channel = sourceMeta.title;
  if (sourceMeta.url) source.url = sourceMeta.url;
  source.lastFetchedAt = now();
  source._updatedAt = Date.now();

  const unmapped = [];
  let imported = 0;

  rawSignals.forEach(raw => {
    const ticker = resolveTickerMap(raw.assetName);
    if (!ticker) unmapped.push(raw.assetName);

    // Normalize levels to TRY
    const supportTRY = _normalizeLevelToTRY(raw.supportLevel, raw.supportCcy, ticker);
    const targetTRY = _normalizeLevelToTRY(raw.targetLevel, raw.targetCcy, ticker);

    // Check for duplicate: same source + same asset name + same day
    const today = now().slice(0, 10);
    const isDupe = S.signals.some(s =>
      s.sourceId === source.id &&
      s.assetName === raw.assetName &&
      (s.createdAt || '').slice(0, 10) === today
    );

    if (isDupe) return; // Skip duplicates

    const signal = {
      id: uid(),
      sourceId: source.id,
      sourceName: sourceMeta.analyst,
      sourceTitle: sourceMeta.title || '',
      sourceUrl: sourceMeta.url || '',
      sourceDate: sourceMeta.date || '',
      ticker: ticker,
      assetName: raw.assetName,
      assetClass: raw.assetClass,
      action: raw.action,
      trend: raw.trend,
      supportLevel: raw.supportLevel,
      supportCcy: raw.supportCcy,
      supportLevelTRY: supportTRY,
      targetLevel: raw.targetLevel,
      targetCcy: raw.targetCcy,
      targetLevelTRY: targetTRY,
      watchTriggers: raw.watchTriggers,
      notes: raw.notes,
      evidencePhrases: raw.evidencePhrases,
      status: 'NEW',
      createdAt: now(),
      _updatedAt: Date.now()
    };

    S.signals.push(signal);
    source.totalSignals++;
    imported++;

    // Track asset classes
    if (!source.assetClasses.includes(raw.assetClass)) {
      source.assetClasses.push(raw.assetClass);
    }
  });

  save();
  return { imported, unmapped };
}


// ============ SIGNAL ACTIONS ============

/**
 * Inline ticker save — from the input on the signal card itself
 */
export function inlineTickerSave(signalId) {
  const input = document.getElementById(`inlineTk_${signalId}`);
  if (!input) return;
  const ticker = input.value.trim().toUpperCase();
  if (!ticker) { toast('Enter a ticker symbol', 'a-r'); return; }

  const sig = S.signals.find(s => s.id === signalId);
  if (!sig) return;

  // Save to tickerMap + update signal
  S.tickerMap[sig.assetName] = ticker;
  sig.ticker = ticker;
  sig.supportLevelTRY = _normalizeLevelToTRY(sig.supportLevel, sig.supportCcy, sig.ticker);
  sig.targetLevelTRY = _normalizeLevelToTRY(sig.targetLevel, sig.targetCcy, sig.ticker);
  sig._updatedAt = Date.now();
  stampItem(S);
  save();
  toast(`${sig.assetName} → ${ticker}`, 'a-g');
  renderSig();
}

/**
 * Re-resolve ALL signals — run 3-layer ticker resolution on every signal
 * Useful after adding new holdings or when BIST_REF is updated
 */
export function reResolveAllSignals() {
  let updated = 0;
  (S.signals || []).forEach(sig => {
    const resolved = resolveTickerMap(sig.assetName);
    if (resolved && resolved !== sig.ticker) {
      sig.ticker = resolved;
      sig.supportLevelTRY = _normalizeLevelToTRY(sig.supportLevel, sig.supportCcy, sig.ticker);
      sig.targetLevelTRY = _normalizeLevelToTRY(sig.targetLevel, sig.targetCcy, sig.ticker);
      sig._updatedAt = Date.now();
      updated++;
    }
  });
  if (updated) {
    stampItem(S);
    save();
    toast(`${updated} signal(s) re-resolved`, 'a-g');
  } else {
    toast('All signals already mapped', 'a-g');
  }
  renderSig();
}

export function deleteSignal(id) {
  if (!confirm('Delete this signal?')) return;
  S.signals = S.signals.filter(s => s.id !== id);
  tombstoneId(id);
  save();
  renderSig();
  toast('Signal deleted', 'a-g');
}

/**
 * Open Trade form pre-filled with signal data
 * Navigates to Trades page and opens the add-trade modal with signal context
 */
export function openTradeFromSignal(signalId) {
  const sig = S.signals.find(s => s.id === signalId);
  if (!sig) return;

  // Mark signal as ACTED
  sig.status = 'ACTED';
  sig.actedAt = now();
  sig._updatedAt = Date.now();
  save();

  // Navigate to Trades page
  if (window.navTo) window.navTo('trades');

  // Wait for trades page to render, then open + pre-fill the modal
  setTimeout(() => {
    if (window.openAddT) window.openAddT();

    setTimeout(() => {
      // Ticker
      const tkEl = document.getElementById('tTk');
      if (tkEl && sig.ticker) tkEl.value = normTicker(sig.ticker);

      // Currency — detect from signal
      const curEl = document.getElementById('tCur');
      if (curEl) curEl.value = sig.supportCcy || 'TRY';

      // Type — BIST equity if .IS ticker
      const tpEl = document.getElementById('tTp');
      if (tpEl) {
        if (sig.ticker?.endsWith('.IS') || sig.assetClass === 'Equity') tpEl.value = 'Equity';
        else if (sig.assetClass === 'Crypto') tpEl.value = 'Crypto';
        else if (sig.assetClass === 'Commodity') tpEl.value = 'Commodity';
        else tpEl.value = 'Equity';
      }

      // Side
      const sdEl = document.getElementById('tSd');
      // Long unless the call is bearish. ⚠ I filed "a WAIT signal prefills Long" as a defect while
      // measuring and then withdrew it: the field is a two-value select with no third option, Long
      // is the form's own default, and a non-directional signal gives no reason to prefer Short.
      // The change here is only that the direction comes from the one definition.
      if (sdEl) sdEl.value = actionDirection(sig.action) === 'bear' ? 'Short' : 'Long';

      // Entry price — current price or support level
      const enEl = document.getElementById('tEn');
      if (enEl) {
        const priceObj = _getSignalPrice(sig);
        enEl.value = priceObj ? priceObj.price : (sig.supportLevel || '');
      }

      // SL — support level
      const slEl = document.getElementById('tSl');
      if (slEl && sig.supportLevel) slEl.value = sig.supportLevel;

      // TP1 — target level
      const t1El = document.getElementById('tT1');
      if (t1El && sig.targetLevel) t1El.value = sig.targetLevel;

      // Note — link back to signal source
      const noEl = document.getElementById('tNo');
      if (noEl) {
        const parts = [];
        if (sig.sourceName) parts.push(sig.sourceName);
        if (sig.sourceTitle) parts.push(sig.sourceTitle);
        if (sig.sourceDate) parts.push(sig.sourceDate);
        noEl.value = `Signal: ${parts.join(' · ')}`;
      }

      // Trigger TD field toggle in case type changed
      if (window.toggleTdTradeFields) window.toggleTdTradeFields();

      // Update modal title
      const titleEl = document.getElementById('moTTitle');
      if (titleEl) titleEl.textContent = `New Trade — ${sig.assetName}`;
    }, 100);
  }, 200);
}

export function expireSignal(id) {
  const sig = S.signals.find(s => s.id === id);
  if (!sig) return;
  sig.status = 'EXPIRED';
  sig.expiredAt = now();
  sig._updatedAt = Date.now();
  save();
  renderSig();
  toast('Signal marked expired', 'a-g');
}


// ============ UI — PASTE MODAL ============

export function openPasteSignal() {
  const ta = document.getElementById('sigHtmlInput');
  if (ta) ta.value = '';
  const res = document.getElementById('sigParseResult');
  if (res) res.innerHTML = '';
  const label = document.getElementById('sigFileLabel');
  if (label) label.textContent = '';
  const mo = document.getElementById('moSignalPaste');
  if (mo) { mo.style.display = ''; mo.classList.add('active'); }

  // Wire file input (only once)
  _initSignalFileHandlers();
}

export function browseSignalFile() {
  const fi = document.getElementById('sigFileInput');
  if (fi) fi.click();
}

let _sigFileHandlersInit = false;
function _initSignalFileHandlers() {
  if (_sigFileHandlersInit) return;
  _sigFileHandlersInit = true;

  const dropZone = document.getElementById('sigDropZone');
  const fileInput = document.getElementById('sigFileInput');

  if (fileInput) {
    fileInput.addEventListener('change', (e) => {
      const file = e.target.files[0];
      if (file) _readSignalFile(file);
      fileInput.value = ''; // reset so same file can be re-selected
    });
  }

  if (dropZone) {
    dropZone.addEventListener('dragover', (e) => {
      e.preventDefault();
      e.stopPropagation();
      dropZone.style.borderColor = 'var(--acc)';
      dropZone.style.background = 'color-mix(in srgb, var(--acc) 10%, transparent)';
    });
    dropZone.addEventListener('dragleave', (e) => {
      e.preventDefault();
      e.stopPropagation();
      dropZone.style.borderColor = 'var(--bd)';
      dropZone.style.background = '';
    });
    dropZone.addEventListener('drop', (e) => {
      e.preventDefault();
      e.stopPropagation();
      dropZone.style.borderColor = 'var(--bd)';
      dropZone.style.background = '';
      const file = e.dataTransfer.files[0];
      if (file) _readSignalFile(file);
    });
  }
}

function _readSignalFile(file) {
  if (!file.name.match(/\.html?$/i)) {
    toast('Please select an HTML file', 'a-r');
    return;
  }

  const reader = new FileReader();
  reader.onload = (e) => {
    const ta = document.getElementById('sigHtmlInput');
    if (ta) ta.value = e.target.result;
    const label = document.getElementById('sigFileLabel');
    if (label) label.textContent = file.name;
  };
  reader.onerror = () => toast('Failed to read file', 'a-r');
  reader.readAsText(file);
}

export function execPasteSignal() {
  const ta = document.getElementById('sigHtmlInput');
  if (!ta || !ta.value.trim()) { toast('Paste HTML content first', 'a-r'); return; }

  const { imported, unmapped } = importSignals(ta.value);

  // Auto-seed unmapped names into tickerMap with empty value (so they show in the map UI)
  const uniqueUnmapped = [...new Set(unmapped)];
  uniqueUnmapped.forEach(name => {
    if (!S.tickerMap[name]) S.tickerMap[name] = '';
  });

  let msg = `Imported ${imported} signal(s)`;
  if (uniqueUnmapped.length) {
    msg += `. ${uniqueUnmapped.length} unmapped — scroll down to Ticker Map`;
  }
  toast(msg, imported > 0 ? 'a-g' : 'a-r');

  clMo('moSignalPaste');
  renderSig();

  // Auto-scroll to ticker map if there are unmapped tickers
  if (uniqueUnmapped.length) {
    setTimeout(() => {
      const mapEl = document.getElementById('sigTickerMap');
      if (mapEl) mapEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 200);
  }
}


// ============ UI — RENDER ============

/**
 * Format level with currency badge
 */
function _fmtLevel(value, ccy, tryValue) {
  if (!value) return '<span style="color:var(--t3)">—</span>';
  let s = `<span style="font-weight:600">${value}</span>`;
  if (ccy) s += ` <span style="color:var(--t3);font-size:11px">${ccy}</span>`;
  if (ccy && ccy !== 'TRY' && tryValue && tryValue !== value) {
    s += ` <span style="color:var(--t3);font-size:10px">(${f(tryValue)} TRY)</span>`;
  }
  return s;
}

/**
 * Status badge color map
 */
function _statusBadge(status) {
  const map = {
    'NEW': 'var(--acc)',
    'APPROACHING': 'var(--salmon)',
    'TRIGGERED': '#FF4D6A',
    'ACTED': 'var(--mint)',
    'EXPIRED': 'var(--t3)'
  };
  const color = map[status] || 'var(--t3)';
  return `<span style="background:${color}20;color:${color};padding:2px 8px;border-radius:10px;font-size:10px;font-weight:600">${status}</span>`;
}

/**
 * Action badge colour — BY DIRECTION, from the one definition.
 *
 * ⚠ THIS USED TO CARRY ITS OWN VOCABULARY AND THE VOCABULARY WAS WRONG. The map read
 * `BUY / SELL / HOLD / **WATCH**`, and the ingest has **never once emitted `WATCH`** — it emits
 * `WAIT`. Measured 2026-08-16, the seven live values are
 * `HOLD · WAIT · BUY · SELL · BUY_ON_CONDITION · BUY_ON_DIP · BUY_WITH_STOP`, so **four of the
 * seven fell through to grey**: the commonest non-directional call and all three conditional
 * buys, uncoloured on the Signals page for as long as the feed has existed.
 *
 * It is routed through `actionDirection` rather than given a longer list of its own, because
 * part 2b introduces that map as the single definition of what an action MEANS and a second
 * spelling next door is the disease the quantity registry exists to catch (rule #24, #48).
 * An action the map has never heard of stays grey — that is the honest answer, not green.
 */
function _actionBadge(action) {
  const byDir = { bull: '#3fb950', bear: '#f85149', flat: '#58a6ff' };
  const color = byDir[actionDirection(action)] || 'var(--t2)';
  return `<span style="color:${color};font-weight:700">${esc(String(action == null ? '' : action))}</span>`;
}

/**
 * Trend badge
 */
function _trendBadge(trend) {
  const map = {
    'UPTREND': { bg: '#1a2e1a', c: '#3fb950' },
    'DOWNTREND': { bg: '#2e1a1a', c: '#f85149' },
    'SIDEWAYS': { bg: '#2e2a1a', c: '#d29922' },
    'SIDEWAYS_TO_UP': { bg: '#1c2533', c: '#58a6ff' }
  };
  const s = map[trend] || { bg: 'var(--bg2)', c: 'var(--t3)' };
  return `<span style="background:${s.bg};color:${s.c};padding:2px 8px;border-radius:10px;font-size:10px;font-weight:500">${trend}</span>`;
}

/**
 * Tickers I currently hold (portfolio) or have an open/partial trade in.
 */
function _sigPositionTickers() {
  const set = new Set();
  (S.holdings || []).forEach(h => { if (h.ticker) set.add(normTicker(h.ticker)); });
  (S.trades || []).forEach(t => { const st = t.status || 'Open'; if (t.ticker && (st === 'Open' || st === 'Partial')) set.add(normTicker(t.ticker)); });
  return set;
}

/**
 * S3 product-group filter pills (data-driven via signalProductGroup, combinable
 * with the existing status/action/source filters). Inline-styled = no CSS dep.
 */
function _sigGroupPills(allActive) {
  const counts = {};
  allActive.forEach(s => { const g = signalProductGroup(s); counts[g] = (counts[g] || 0) + 1; });
  const groups = Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
  if (groups.length < 2) return ''; // nothing to filter if only one group
  const pill = (label, val, n) => {
    const on = _sigFilter.productGroup === val;
    const bg = on ? 'rgba(0,240,255,.16)' : 'rgba(255,255,255,.03)';
    const bd = on ? 'var(--cyan)' : 'var(--bd)';
    const col = on ? 'var(--cyan)' : 'var(--t2)';
    return `<button data-action="setSignalFilter" data-args='["productGroup","${esc(val)}"]' style="background:${bg};border:1px solid ${bd};border-radius:999px;padding:5px 12px;cursor:pointer;font-size:12px;color:${col};font-weight:${on ? '600' : '500'}">${esc(label)}${n != null ? ` <span style="opacity:.55">${n}</span>` : ''}</button>`;
  };
  return `<div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:12px">
    ${pill('All groups', 'ALL', allActive.length)}
    ${groups.map(g => pill(g, g, counts[g])).join('')}
  </div>`;
}

/**
 * S4 (part 1) — per-analyst activity + specialty stats from existing signal data.
 * NOTE: this is the reliable half (coverage/specialty/activity). The hit-rate
 * confidence SCORE needs outcome tracking (AI-extracted SL + price history) and is
 * computed separately once that data exists — we do NOT fabricate a score here.
 */
function signalAnalystStats() {
  const sigs = (S.signals || []).filter(s => s.status !== 'EXPIRED');
  const map = {};
  sigs.forEach(s => {
    const a = s.sourceName || 'Unknown';
    if (!map[a]) map[a] = { name: a, total: 0, byGroup: {}, triggered: 0, acted: 0, lastDate: '' };
    const m = map[a];
    m.total++;
    const g = signalProductGroup(s);
    m.byGroup[g] = (m.byGroup[g] || 0) + 1;
    if (s.status === 'TRIGGERED') m.triggered++;
    if (s.status === 'ACTED') m.acted++;
    const d = s.sourceDate || (s.createdAt || '').slice(0, 10);
    if (d && d > m.lastDate) m.lastDate = d;
  });
  return Object.values(map).sort((a, b) => b.total - a.total);
}

/**
 * Find an OPEN/PARTIAL trade whose ticker matches this signal (for TP-sync).
 */
function _signalOpenTrade(sig) {
  // One definition of "is there an open trade in this name" — see _openTradeForTicker. This was
  // a second hand-written copy of that loop within minutes of the first, which is exactly what
  // the ticker registry row exists to stop.
  return sig ? _openTradeForTicker(sig.ticker) : null;
}

/**
 * Apply a signal's suggested target to the matching open trade's TP1 (user-confirmed).
 * Currency-safe: refuses when the signal target currency differs from the trade's.
 */
export async function applySignalTP(signalId) {
  const sig = (S.signals || []).find(s => s.id === signalId);
  if (!sig) return;
  const tr = _signalOpenTrade(sig);
  if (!tr) return toast('No matching open trade', 'a-r');
  if (!sig.targetLevel) return toast('Signal has no target level', 'a-r');
  const ccy = tr.currency || 'TRY';
  if (sig.targetCcy && sig.targetCcy !== ccy) return toast(`Currency mismatch (${sig.targetCcy} vs ${ccy}) — edit the trade manually`, 'a-r');
  const old = tr.tp1;
  const ok = await uiConfirm(`Update ${esc(tr.ticker)} trade TP1 from ${fByCur(ccy, old || 0)} to ${fByCur(ccy, sig.targetLevel)}?`, { title: 'Apply signal TP', okText: 'Apply TP' });
  if (!ok) return;
  tr.tp1 = sig.targetLevel;
  tr._updatedAt = Date.now();
  stampItem(S);
  save();
  toast(`${tr.ticker} TP1 → ${fByCur(ccy, sig.targetLevel)}`, 'a-g');
  if (window.markDirty) window.markDirty('trades');
  if (typeof window.renderT === 'function') window.renderT();
  renderSig();
}

/**
 * Snapshot outcome for a signal: current price vs target (TP) and a default-SL floor
 * derived from the support/entry level (approved rule: 25% adverse). Returns 'tp' | 'sl' | null.
 * NOTE: path-INSENSITIVE — it reflects current standing, not whether TP was hit before SL.
 * A forward-observed (immutable on price-cross) engine is the later refinement for path accuracy.
 */
function _resolveSignalOutcome(sig) {
  const entry = sig.supportLevelTRY || null;
  const tp = sig.targetLevelTRY || null;
  if (!entry || !tp) return null;
  const p = _getSignalPrice(sig);
  const cur = _priceTRY(p);
  if (!cur) return null;
  const _dir = actionDirection(sig.action);
  const isBuy = _dir === 'bull' || (!sig.action && (sig.trend || '').includes('UP'));
  if (isBuy) {
    if (cur >= tp) return 'tp';
    if (cur <= entry * 0.75) return 'sl';
  } else if (_dir === 'bear') {
    if (cur <= tp) return 'tp';
    if (cur >= entry * 1.25) return 'sl';
  }
  return null;
}

/**
 * Per-analyst confidence from FORWARD-OBSERVED outcomes (sig._outcome, frozen on first TP/SL
 * cross after trigger). Bayesian-shrunk hit-rate (prior 50%, pseudo-count 4) so thin samples
 * never read as 100%. n always surfaced. Counts only resolved signals — stationary, path-accurate.
 */
function signalAnalystConfidence() {
  const sigs = (S.signals || []).filter(s => s.status !== 'EXPIRED');
  const m = {};
  sigs.forEach(s => {
    const a = s.sourceName || 'Unknown';
    if (!m[a]) m[a] = { resolved: 0, tp: 0, sl: 0 };
    const o = s._outcome; // frozen forward-observed outcome (set once in updateSignalStatuses)
    if (o === 'tp') { m[a].resolved++; m[a].tp++; }
    else if (o === 'sl') { m[a].resolved++; m[a].sl++; }
  });
  Object.values(m).forEach(x => { x.score = x.resolved ? Math.round((x.tp + 2) / (x.resolved + 4) * 100) : null; });
  return m;
}

/**
 * Collapsible analyst-profiles panel — coverage bars by product group (specialty),
 * activity counts, last-active, + snapshot confidence (with n; provisional when thin).
 */
function _sigAnalystPanel() {
  const stats = signalAnalystStats();
  if (!stats.length) return '';
  const conf = signalAnalystConfidence();
  const rows = stats.map(a => {
    const c = conf[a.name];
    const confHtml = (c && c.resolved)
      ? ` · <span style="color:${c.score >= 60 ? 'var(--grn)' : c.score >= 45 ? 'var(--yel)' : 'var(--red)'};font-weight:600">conf ${c.score}/100</span> <span style="opacity:.6">n=${c.resolved}${c.resolved < 5 ? ' · provisional' : ''}</span>`
      : ` · <span style="opacity:.5">conf: tracking…</span>`;
    const groups = Object.entries(a.byGroup).sort((x, y) => y[1] - x[1]);
    const top = groups[0] ? groups[0][0] : '—';
    const bars = groups.slice(0, 5).map(([g, n]) => {
      const pct = Math.round(n / a.total * 100);
      return `<div style="display:flex;align-items:center;gap:8px;margin:3px 0">
        <span style="font-size:10px;color:var(--t3);width:96px;flex-shrink:0">${esc(g)}</span>
        <div style="flex:1;height:6px;background:rgba(255,255,255,.05);border-radius:3px;overflow:hidden"><div style="width:${pct}%;height:100%;background:var(--vio)"></div></div>
        <span style="font-size:10px;color:var(--t2);width:30px;text-align:right">${n}</span>
      </div>`;
    }).join('');
    return `<div style="padding:10px 0;border-bottom:1px solid var(--bd)">
      <div style="display:flex;justify-content:space-between;align-items:baseline;margin-bottom:5px">
        <span style="font-weight:600;color:var(--t1)">${esc(a.name)}</span>
        <span style="font-size:11px;color:var(--t3)">${a.total} signals · specialty <span style="color:var(--vio);font-weight:600">${esc(top)}</span></span>
      </div>
      ${bars}
      <div style="font-size:10px;color:var(--t3);margin-top:5px">${a.triggered} triggered · ${a.acted} acted${a.lastDate ? ` · last ${esc(a.lastDate)}` : ''}${confHtml}</div>
    </div>`;
  }).join('');
  return `<details class="jrn-more" style="margin-bottom:16px"><summary style="cursor:pointer;color:var(--t2);font-size:12px;font-weight:600;padding:6px 0">Analyst profiles — coverage &amp; specialty (${stats.length})</summary><div class="card" style="margin-top:8px;padding:12px 16px">${rows}</div></details>`;
}

/**
 * Group key for a signal under the current groupBy axis (analyst / product / source).
 */
function _sigGroupKey(sig) {
  if (_sigFilter.groupBy === 'analyst') return sig.sourceName || 'Unknown analyst';
  if (_sigFilter.groupBy === 'product') return signalProductGroup(sig);
  if (_sigFilter.groupBy === 'source') return (sig.sourceId || '').startsWith('mongo:') ? 'MongoDB' : 'HTML';
  return '';
}

/**
 * Group-by control (None / Analyst / Product / Source). Inline-styled neon pills.
 */
function _sigGroupByControl() {
  const opts = [['none', 'No grouping'], ['analyst', 'By analyst'], ['product', 'By product'], ['source', 'By source']];
  const pill = ([val, label]) => {
    const on = _sigFilter.groupBy === val;
    return `<button data-action="setSignalFilter" data-args='["groupBy","${val}"]' style="background:${on ? 'rgba(167,139,250,.18)' : 'rgba(255,255,255,.03)'};border:1px solid ${on ? 'var(--vio)' : 'var(--bd)'};border-radius:999px;padding:4px 11px;cursor:pointer;font-size:11px;color:${on ? 'var(--vio)' : 'var(--t3)'};font-weight:${on ? '600' : '500'}">${label}</button>`;
  };
  return `<div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:12px;align-items:center"><span style="font-size:10px;color:var(--t3);text-transform:uppercase;margin-right:2px">Group</span>${opts.map(pill).join('')}</div>`;
}

/**
 * Render the Signals page
 */
/**
 * Every live signal about one ticker, spelling-normalised.
 *
 * The shared selector for the position↔signal join. `_signalOpenTrade` goes signal → trade and
 * this goes ticker → signals; both route the spelling through `utils.normTicker` (`ae5e02a`),
 * which is what makes the join work at all — before it, `ASELS.IS` against `ASELS` returned
 * **zero** matches across his 8 open trades and 34 signal tickers.
 */
export function signalsForTicker(ticker) {
  const tk = normTicker(ticker);
  if (!tk) return [];
  return (S.signals || []).filter(s => s && s.status !== 'EXPIRED' && s.ticker && normTicker(s.ticker) === tk);
}

/**
 * TRADES PART 2b — what the tape says about one open trade.
 *
 * The thin half: read `S`, do the join, hand the rows to the pure decider in
 * `core/signal-stance.js`. Returns `null` when the tape has never mentioned the ticker, which
 * means **render nothing** — see that file's docblock.
 *
 * ⚠ **THIS DELIBERATELY DOES NOT INHERIT `positionEventSignals`' STATUS FILTER.** That feed asks
 * *"what is about to happen to a level"* and so keeps only `TRIGGERED` / `APPROACHING`. Measured
 * 2026-08-16: **`APPROACHING` never occurs in his data**, so of the **38** signals touching a
 * position it surfaces **4** — and the ASELS one it surfaces is the two-week-old SELL the same
 * analyst has since reversed. The stance asks a different question, *"what does the tape think of
 * this position"*, and needs the `NEW` rows the other feed drops. Same join, same normaliser,
 * different question — extended, not duplicated.
 */
export function tradeSignalStance(trade) {
  if (!trade || !trade.ticker) return null;
  return signalStance(trade, signalsForTicker(trade.ticker));
}

/**
 * The same question for a PORTFOLIO HOLDING — the larger of the two surfaces.
 *
 * Measured 2026-08-16: **8 of his 44 holding tickers carry a signal**, against 2 of 8 trades, and
 * the strongest conflict in the whole book is here rather than on Trades — **TUPRS**, whose latest
 * call is a SELL and which carries **two unresolved TRIGGERED SELLs** on top of it.
 *
 * ⚠ **A HOLDING HAS NO `side`, AND IS NOT GIVEN A FAKE FIELD.** `signalStance` reads
 * `subject.side`, so the subject is built here, explicitly long, in one line — because a position
 * you own can only be hurt by a bearish call. Writing `side` onto the holding row instead would
 * put a trade's concept into the holdings model and into every serialiser that touches it.
 * ⚠ A short is not expressible in the holdings model at all, so the mirrored half stays proved by
 * tests only. Same as on Trades, where all eighteen rows are `Long`.
 *
 * `alsoOpenTrade` exists because **ASELS is both** — he holds it and runs an open trade on it, so
 * the identical 30 July SELL is unresolved against both positions. Both cards are shown, because
 * they are different positions with different sizes and exits and a panel that silently omits a
 * live conflict is the worse failure; the flag lets the holdings card say where the reasoning
 * already is instead of repeating it.
 */
export function holdingSignalStance(holding) {
  if (!holding || !holding.ticker) return null;
  const st = signalStance({ ticker: holding.ticker, side: 'Long' }, signalsForTicker(holding.ticker));
  if (!st) return null;
  st.alsoOpenTrade = !!_openTradeForTicker(holding.ticker);
  return st;
}

/** Is there an open or partial trade in this ticker? Same normalisation as every other join. */
function _openTradeForTicker(ticker) {
  const tk = normTicker(ticker);
  if (!tk) return null;
  return (S.trades || []).find(t => {
    const st = t.status || 'Open';
    return (st === 'Open' || st === 'Partial') && t.ticker && normTicker(t.ticker) === tk;
  }) || null;
}

/**
 * Position event-risk feed (shared, pure, no DOM): signals that affect my positions AND are
 * triggered/approaching, sorted (triggered first, then closest to level). Single source for
 * the Action Queue (Signals page) and the Event Risk panel (Risk page).
 * Returns { items:[{sig,prox}], triggered, watch }.
 */
export function positionEventSignals() {
  const allActive = (S.signals || []).filter(s => s.status !== 'EXPIRED');
  const pos = _sigPositionTickers();
  const proxOf = s => {
    const cur = _priceTRY(_getSignalPrice(s));
    const lvl = actionDirection(s.action) === 'bear' ? s.targetLevelTRY : (s.supportLevelTRY || s.targetLevelTRY);
    const d = cur && lvl ? _pctDistance(cur, lvl) : null;
    return d == null ? Infinity : Math.abs(d);
  };
  const items = allActive
    .filter(s => s.ticker && pos.has(normTicker(s.ticker)) &&
      (s.status === 'TRIGGERED' || s.status === 'APPROACHING') &&
      (s.supportLevel || s.targetLevel || s._aiExtracted || _signalOpenTrade(s)))
    .map(s => ({ sig: s, prox: proxOf(s) }))
    .sort((a, b) => ((a.sig.status === 'TRIGGERED' ? 0 : 1) - (b.sig.status === 'TRIGGERED' ? 0 : 1)) || (a.prox - b.prox));
  const triggered = items.filter(i => i.sig.status === 'TRIGGERED').length;
  return { items, triggered, watch: items.length - triggered };
}

/**
 * Consensus & Conflict — where analysts AGREE on an instrument (strong signal) vs DISAGREE
 * (caution). Counts distinct analysts per direction. Pure; reuses existing signal data.
 */
function _sigConsensus() {
  const allActive = (S.signals || []).filter(s => s.status !== 'EXPIRED');
  const byTk = {};
  allActive.forEach(s => {
    if (!s.ticker) return;
    const act = (s.action || '').toUpperCase();
    if (act !== 'BUY' && act !== 'SELL') return; // directional calls only
    if (!byTk[s.ticker]) byTk[s.ticker] = { ticker: s.ticker, name: s.assetName || s.ticker, buy: new Set(), sell: new Set() };
    (act === 'BUY' ? byTk[s.ticker].buy : byTk[s.ticker].sell).add(s.sourceName || '?');
  });
  const items = Object.values(byTk).map(x => ({ ticker: x.ticker, name: x.name, buy: x.buy.size, sell: x.sell.size, total: x.buy.size + x.sell.size }));
  const consensus = items.filter(i => i.total >= 2 && (i.buy === 0 || i.sell === 0)).sort((a, b) => b.total - a.total).slice(0, 5);
  const conflict = items.filter(i => i.buy > 0 && i.sell > 0).sort((a, b) => b.total - a.total).slice(0, 5);

  const dots = (buy, sell) => {
    let d = '';
    for (let i = 0; i < buy; i++) d += '<span style="display:inline-block;width:7px;height:7px;border-radius:50%;background:#34e2a0;margin-left:3px"></span>';
    for (let i = 0; i < sell; i++) d += '<span style="display:inline-block;width:7px;height:7px;border-radius:50%;background:#ff5470;margin-left:3px"></span>';
    return d;
  };
  const dir = i => i.sell === 0 ? '<span style="color:var(--grn)">BUY</span>' : i.buy === 0 ? '<span style="color:var(--red)">SELL</span>' : '<span style="color:var(--yel)">SPLIT</span>';
  const row = i => `<div style="display:flex;align-items:center;gap:10px;padding:8px 0;border-bottom:1px solid rgba(120,140,200,.07)">
    <span class="tk" style="width:30px;height:30px;font-size:9px">${esc(normTicker(i.ticker).slice(0, 4))}</span>
    <div style="flex:1;min-width:0"><div style="font-size:12px;font-weight:600;color:var(--t1)">${esc(i.name)} <span style="font-size:10px;font-family:var(--mono)">${dir(i)}</span></div>
      <div style="font-size:10px;font-family:var(--mono);color:var(--t3)">${i.buy} BUY · ${i.sell} SELL · ${i.total} analyst${i.total > 1 ? 's' : ''}</div></div>
    <div style="white-space:nowrap">${dots(i.buy, i.sell)}</div>
  </div>`;
  const empty = m => `<div class="neo-empty">${m}</div>`; // S2b: shared empty-state class

  return `<div class="spk" style="margin-bottom:16px">
    <div style="display:flex;justify-content:space-between;align-items:baseline;margin-bottom:10px">
      <h2 class="pt" style="margin:0">Consensus &amp; Conflict</h2>
      <span style="font-size:10px;color:var(--t3);font-family:var(--mono)">where analysts agree / disagree on the same name</span>
    </div>
    <div class="row" style="gap:16px;flex-wrap:wrap;align-items:flex-start">
      <div style="flex:1;min-width:280px">
        <div style="font-size:11px;color:var(--grn);font-weight:600;margin-bottom:4px">✓ Consensus — strong agreement</div>
        ${consensus.length ? consensus.map(row).join('') : empty('No instrument with 2+ analysts agreeing yet')}
      </div>
      <div style="flex:1;min-width:280px">
        <div style="font-size:11px;color:var(--yel);font-weight:600;margin-bottom:4px">⚠ Conflict — analysts disagree</div>
        ${conflict.length ? conflict.map(row).join('') : empty('No conflicting calls right now')}
      </div>
    </div>
  </div>`;
}

// ─── Signals rework v0.2 — presentation layer (2026-07-07) ───
// Widget-first: header · lens · hero (top position-touching) · confidence-ranked
// queue · consensus · 3 analytics axes · filter/group + expired + ticker-map
// disclosures. Reuses the canonical .mny-* shell; signals-unique styles are .sig-*.
// Pure presentation — every action, id and data contract from the parity contract
// (2026-07-06) is preserved. positionEventSignals() still feeds the Risk page.

const _sigN = v => (v == null || !isFinite(v)) ? '' : (Math.abs(v) >= 1000 ? Math.round(v).toLocaleString('en-US') : (+v).toFixed(2));

/** Bayesian-shrunk hit-rate (prior 50%, pseudo-count 4) by arbitrary key from frozen
 *  forward-observed outcomes — same math as signalAnalystConfidence so all three
 *  analytics axes (analyst/product/source) stay mutually consistent. */
function _sigConfBy(keyFn) {
  const m = {};
  (S.signals || []).filter(s => s.status !== 'EXPIRED').forEach(s => {
    const o = s._outcome;
    if (o !== 'tp' && o !== 'sl') return;
    const k = keyFn(s) || '—';
    if (!m[k]) m[k] = { n: 0, tp: 0 };
    m[k].n++; if (o === 'tp') m[k].tp++;
  });
  return Object.entries(m).map(([key, v]) => ({ key, n: v.n, score: Math.round((v.tp + 2) / (v.n + 4) * 100) }))
    .sort((a, b) => b.score - a.score || b.n - a.n);
}

/** SL · now · target mapped along observed min..max (TRY space). Illustrative rail. */
function _sigRail(sl, cur, target) {
  const pts = [sl, cur, target].filter(v => v != null && isFinite(v));
  if (pts.length < 2 || cur == null || !isFinite(cur)) return '';
  const lo = Math.min(...pts), hi = Math.max(...pts), span = (hi - lo) || 1;
  const x = v => Math.max(3, Math.min(97, (v - lo) / span * 100));
  const mk = (v, lbl) => (v == null || !isFinite(v)) ? '' :
    `<div class="sig-pt" style="left:${x(v)}%"></div><div class="sig-rlb" style="left:${x(v)}%">${lbl}</div>`;
  return `<div class="sig-rail" aria-hidden="true"><div class="sig-tr"></div>
    ${mk(sl, 'SL ' + _sigN(sl))}
    <div class="sig-cur" style="left:${x(cur)}%"></div><div class="sig-rlb cy" style="left:${x(cur)}%">now ${_sigN(cur)}</div>
    ${mk(target, 'target ' + _sigN(target))}</div>`;
}

/** Analyst specialty (top product group) for the confidence chip. */
function _sigSpecialty(name) {
  const st = signalAnalystStats().find(a => a.name === name);
  if (!st) return null;
  const g = Object.entries(st.byGroup).sort((a, b) => b[1] - a[1])[0];
  return g ? g[0] : null;
}

/** One-click action for a signal (shared by hero + queue). */
function _sigActionBtn(sig) {
  const ot = _signalOpenTrade(sig);
  if (ot && sig.targetLevel && ot.tp1) {
    const ccy = ot.currency || 'TRY';
    const same = !sig.targetCcy || sig.targetCcy === ccy;
    if (same && Math.abs(sig.targetLevel - ot.tp1) / ot.tp1 > 0.005) {
      const dp = (sig.targetLevel - ot.tp1) / ot.tp1 * 100;
      return { label: `Sync TP (${dp >= 0 ? '+' : ''}${dp.toFixed(1)}%)`, action: 'applySignalTP', ac: 'var(--vio)' };
    }
  }
  if (!sig.ticker) return { label: 'Map ticker', action: null, ac: 'var(--yel)' };
  if (sig.status !== 'ACTED') return { label: 'Open trade', action: 'openTradeFromSignal', ac: 'var(--grn)' };
  return null;
}

/** Confidence-first rank: analyst hit-rate + status urgency + position relevance. */
function _sigRank(sig, cmap) {
  const conf = cmap[sig.sourceName || 'Unknown'];
  const c = (conf && conf.resolved) ? conf.score : 50;
  const trig = sig.status === 'TRIGGERED' ? 40 : sig.status === 'APPROACHING' ? 20 : 0;
  const held = (_signalOpenTrade(sig) || (sig.ticker && _sigPositionTickers().has(normTicker(sig.ticker)))) ? 15 : 0;
  return c + trig + held;
}

/** Hero: the single top position-touching signal (source of Risk event feed too). */
function _sigHeroHTML() {
  const ev = positionEventSignals();
  const sig = ev.items[0] && ev.items[0].sig;
  if (!sig) {
    return `<div class="sig-lbl">Top signal · touches your position</div>
      <div class="sig-empty">No signal is touching your positions right now. "No signal" is a valid answer — switch to <b>All active</b> to browse everything, or import a fresh report.</div>`;
  }
  const held = _sigPositionTickers().has(normTicker(sig.ticker));
  const ot = _signalOpenTrade(sig);
  const p = _getSignalPrice(sig);
  const nowTRY = _priceTRY(p);
  const conf = signalAnalystConfidence()[sig.sourceName || 'Unknown'];
  const spec = _sigSpecialty(sig.sourceName || 'Unknown');
  const trig = sig.status === 'TRIGGERED';
  // ⚠ SAME DISEASE, DISPLAY SIDE. `sig.targetCcy || 'TRY'` printed SMH's $670 target as ₺670.
  // The instrument's own quote currency is the fallback, not the lira.
  const _lvlCcy = _levelCcy(sig.ticker);
  const tgtTxt = sig.targetLevel ? fByCur(sig.targetCcy || _lvlCcy || 'TRY', sig.targetLevel) : '—';
  const supTxt = sig.supportLevel ? fByCur(sig.supportCcy || _lvlCcy || 'TRY', sig.supportLevel) : '—';
  // ⚠ A NON-DIRECTIONAL CALL USED TO GET THE GREEN ▲. Anything that was not literally SELL fell
  // into the buy arm, so his 64 WAIT signals and 9 HOLDs rendered as bullish.
  const _adir = actionDirection(sig.action);
  const actBadge = _adir === 'bear'
    ? `<span class="sig-badge sell">▼ ${esc(sig.action || 'SELL')}</span>`
    : _adir === 'bull'
      ? `<span class="sig-badge buy">▲ ${esc(sig.action || 'BUY')}</span>`
      : `<span class="sig-badge flat">■ ${esc(sig.action || '—')}</span>`;
  const posBadge = ot ? `<span class="sig-badge held">◆ OPEN TRADE${ot.leverage > 1 ? ' ' + ot.leverage + 'x' : ''}</span>` : held ? '<span class="sig-badge held">◆ YOU HOLD</span>' : '';
  const statusBadge = trig ? '<span class="sig-badge trig">● AT LEVEL</span>' : sig.status === 'APPROACHING' ? '<span class="sig-badge appr">● APPROACHING</span>' : '';
  const confChip = (conf && conf.resolved)
    ? `<span>analyst <b>${esc(sig.sourceName || '—')}</b></span><span class="sig-cbar"><i style="width:${conf.score}%"></i></span><span><b class="grn">${conf.score}%</b> hit rate</span><span class="nn">n=${conf.resolved}${conf.resolved < 5 ? ' · provisional' : ''}</span>${spec ? `<span class="sp">· ${esc(spec)} specialist</span>` : ''}`
    : `<span>analyst <b>${esc(sig.sourceName || '—')}</b></span><span class="nn">confidence tracking…</span>${spec ? `<span class="sp">· ${esc(spec)} specialist</span>` : ''}`;
  const btn = _sigActionBtn(sig);
  const btnHtml = (btn && btn.action) ? `<button class="sig-go" data-action="${btn.action}" data-args='["${sig.id}"]'>${esc(btn.label)}</button>` : '';
  const openBtn = (btn && btn.action === 'applySignalTP' && sig.ticker && sig.status !== 'ACTED') ? `<button class="sig-go2" data-action="openTradeFromSignal" data-args='["${sig.id}"]'>Open trade from signal</button>` : '';
  const srcMeta = `source: ${esc(sig.sourceName || 'import')}${sig.sourceDate ? ' · ' + esc(sig.sourceDate) : ''} · currency ${esc(sig.targetCcy || _levelCcy(sig.ticker) || 'TRY')}`;
  return `<div class="sig-lbl">Top signal · touches your position</div>
  <div class="sig-hero">
    <div class="sig-htop">
      <span class="sig-htk">${esc(normTicker(sig.ticker) || sig.assetName || '')}</span>
      <span class="sig-htitle">${esc(sig.action || 'BUY')} — target ${tgtTxt}${trig ? ', at level' : sig.status === 'APPROACHING' ? ', approaching trigger' : ''}</span>
      ${actBadge}${posBadge}${statusBadge}
    </div>
    <div class="sig-hsub">${esc(sig.assetName || sig.ticker || '')} — support ${supTxt}, target <b>${tgtTxt}</b>.${ot && ot.tp1 ? ` Your open trade TP1 is ${fByCur(ot.currency || 'TRY', ot.tp1)}.` : ''}</div>
    <div class="sig-conf">${confChip}</div>
    ${_sigRail(sig.supportLevelTRY, nowTRY, sig.targetLevelTRY)}
    <div class="sig-hbtns">${btnHtml}${openBtn}<span class="sig-hwhy">${srcMeta}</span></div>
  </div>`;
}

/** Three analytics axes (analyst / product / source) + consensus widget. */
function _sigAnalyticsStrip() {
  const byAnalyst = signalAnalystConfidence();
  const aRows = Object.entries(byAnalyst).filter(([, v]) => v.resolved)
    .map(([name, v]) => ({ key: name, n: v.resolved, score: v.score, spec: _sigSpecialty(name) }))
    .sort((a, b) => b.score - a.score).slice(0, 5);
  const byProduct = _sigConfBy(signalProductGroup).slice(0, 5);
  const bySource = _sigConfBy(s => (s.sourceId || '').startsWith('mongo:') ? 'MongoDB feed' : (s.sourceName || 'HTML import')).slice(0, 5);
  const arow = r => `<div class="arow"><span class="nm">${esc(r.key)}${r.spec ? ` <span class="sp">${esc(r.spec)}</span>` : ''}</span><span class="abar"><i style="width:${r.score}%"></i></span><span class="hr">${r.score}%</span><span class="nn">n=${r.n}</span></div>`;
  const panel = (label, rows) => `<div class="mny-spanel"><div class="l">${label}</div>${rows || '<div class="sig-none">no resolved outcomes yet</div>'}</div>`;
  return `<div class="sig-lbl">Consensus &amp; analytics</div>
    ${_sigConsensus()}
    <div class="mny-strip">
      ${panel('By analyst · forward-observed hit rate', aRows.map(arow).join(''))}
      ${panel('By product', byProduct.map(arow).join(''))}
      ${panel('By source', bySource.map(arow).join(''))}
    </div>`;
}

/** A confidence-ranked queue row (reuses the .mny-row shell + shared mnyToggle). */
function _sigQueueRow(sig) {
  const id = 'sigrow-' + sig.id;
  const _qdir = actionDirection(sig.action);
  const ac = _qdir === 'bull' ? 'var(--grn)' : _qdir === 'bear' ? 'var(--red)' : 'var(--cyan)';
  const p = _getSignalPrice(sig);
  const conf = signalAnalystConfidence()[sig.sourceName || 'Unknown'];
  const confTxt = (conf && conf.resolved) ? `<b>${conf.score}%</b> n=${conf.resolved}` : 'tracking';
  const btn = _sigActionBtn(sig);
  const btnHtml = btn
    ? (btn.action
        ? `<button class="sig-rgo" style="--rc:${btn.ac}" data-action="${btn.action}" data-args='["${sig.id}"]'>${esc(btn.label)}</button>`
        : `<span class="sig-rgo static" style="--rc:${btn.ac}">unmapped</span>`)
    : '';
  const proxLbl = sig.status === 'TRIGGERED' ? ' <span class="red">· at level</span>' : sig.status === 'APPROACHING' ? ' <span class="yel">· approaching</span>' : '';
  const holdMark = _signalOpenTrade(sig) ? ' · you hold' : (sig.ticker && _sigPositionTickers().has(normTicker(sig.ticker)) ? ' · you hold' : '');
  const title = `<b>${esc(sig.action || '')}</b> ${esc(sig.assetName || sig.ticker || '')}${sig.targetLevel ? ' · tgt ' + esc(_sigN(sig.targetLevel)) : ''}${holdMark}${proxLbl}`;

  const nowTRY = _priceTRY(p);
  const supDist = nowTRY && sig.supportLevelTRY ? _pctDistance(nowTRY, sig.supportLevelTRY) : null;
  const tgtDist = nowTRY && sig.targetLevelTRY ? _pctDistance(nowTRY, sig.targetLevelTRY) : null;
  const levels = `<div class="sig-lvls">
    <div class="sig-lvl"><div class="k">Support / entry</div><div class="v">${_fmtLevel(sig.supportLevel, sig.supportCcy, sig.supportLevelTRY)}</div>${_proximityBar(supDist, 'support')}</div>
    <div class="sig-lvl"><div class="k">Target</div><div class="v">${_fmtLevel(sig.targetLevel, sig.targetCcy, sig.targetLevelTRY)}</div>${_proximityBar(tgtDist, 'target')}</div>
  </div>`;

  const inPortfolio = sig.ticker ? (S.holdings || []).find(h => normTicker(h.ticker) === normTicker(sig.ticker)) : null;
  let posBlock = '';
  if (inPortfolio) {
    const val = hVal(inPortfolio), pnl = hPnl(inPortfolio), posCur = inPortfolio.currency || 'TRY';
    const col = pnl.pnl != null ? (pnl.pnl >= 0 ? 'grn' : 'red') : '';
    posBlock = `<div class="sig-pos"><div><div class="k">Position</div><div class="v">${inPortfolio.qty || 0} @ ${fByCur(posCur, inPortfolio.avgCost || 0)}</div></div>
      <div><div class="k">Value</div><div class="v">${val.vL != null ? f(val.vL, 0) : '—'} ${esc(posCur)}</div></div>
      <div><div class="k">P&amp;L</div><div class="v ${col}">${pnl.pnlP != null ? (pnl.pnlP >= 0 ? '+' : '') + pnl.pnlP.toFixed(1) + '%' : '—'}</div></div></div>`;
  }

  const srcParts = [];
  if (sig.sourceName) srcParts.push(esc(sig.sourceName));
  if (sig.sourceTitle) srcParts.push(sig.sourceUrl ? `<a href="${esc(safeUrl(sig.sourceUrl))}" target="_blank" rel="noopener">${esc(sig.sourceTitle)}</a>` : esc(sig.sourceTitle));
  if (sig.sourceDate) srcParts.push(esc(sig.sourceDate));
  const srcLine = srcParts.length ? `<div class="sig-src">${srcParts.join(' · ')}</div>` : '';

  const mapRow = !sig.ticker
    ? `<div class="sig-map"><input id="inlineTk_${sig.id}" placeholder="TICKER.IS"><button class="sig-mapbtn" data-action="inlineTickerSave" data-args='["${sig.id}"]'>Map</button><span class="sig-maphint">unmapped — set symbol to enable price + trade</span></div>`
    : '';
  const watch = (sig.watchTriggers && sig.watchTriggers.length) ? `<div class="sig-subs"><div class="k">Watch triggers</div>${sig.watchTriggers.map(t => `<div class="sig-note">${esc(t)}</div>`).join('')}</div>` : '';
  const notes = (sig.notes && sig.notes.length) ? `<div class="sig-subs"><div class="k">Notes</div>${sig.notes.map(t => `<div class="sig-note">${esc(t)}</div>`).join('')}</div>` : '';

  const acts = [];
  if (btn && btn.action) acts.push(`<button class="sig-ra" style="--rc:${btn.ac}" data-action="${btn.action}" data-args='["${sig.id}"]'>${esc(btn.label)} →</button>`);
  acts.push(`<button class="sig-ra exp" data-action="expireSignal" data-args='["${sig.id}"]'>Expire</button>`);
  acts.push(`<button class="sig-ra del" data-action="deleteSignal" data-args='["${sig.id}"]'>Delete</button>`);

  return `<div class="mny-row" id="${id}" style="--ac:${ac}">
    <div class="mny-rmain" data-action="mnyToggle" data-args='["${id}"]'>
      <span class="dot"></span><span class="sig-rtk">${esc(normTicker(sig.ticker) || '—')}</span>
      <span class="sig-rt">${title}</span>
      <span class="sig-rconf">${confTxt}</span>
      ${btnHtml}
      <span class="chev">▾</span>
    </div>
    <div class="mny-rdetail"><div class="mny-rinner">
      ${mapRow}${levels}${posBlock}${srcLine}${watch}${notes}
      <div class="sig-racts">${acts.join('')}</div>
    </div></div>
  </div>`;
}

/** Slim expired row (delete only). */
function _sigExpiredRow(sig) {
  return `<div class="sig-exp"><span><b>${esc(sig.assetName || sig.ticker || '')}</b> <span class="mono">${esc(normTicker(sig.ticker))}${sig.action ? ' · ' + esc(sig.action) : ''}${sig.sourceName ? ' · ' + esc(sig.sourceName) : ''}${sig.sourceDate ? ' · ' + esc(sig.sourceDate) : ''}</span></span><button class="sig-mini del" data-action="deleteSignal" data-args='["${sig.id}"]'>Delete</button></div>`;
}

/** Ticker Map disclosure — preserves .tm-row/.tm-name/.tm-ticker contract for saveTickerMapping. */
function _sigTickerMapHTML() {
  const entries = Object.entries(S.tickerMap || {});
  const unmapped = entries.filter(([, v]) => !v).length;
  const sorted = [...entries].sort((a, b) => (!a[1] && b[1]) ? -1 : (a[1] && !b[1]) ? 1 : a[0].localeCompare(b[0]));
  const rows = sorted.length ? sorted.map(([name, tk]) => {
    const miss = !tk;
    return `<div class="tm-row sig-tmrow${miss ? ' miss' : ''}"><span class="tm-name nm">${esc(name)}</span><input class="tm-ticker" value="${esc(tk)}" placeholder="e.g. ${esc(name.toUpperCase().replace(/\s+/g, ''))}.IS"></div>`;
  }).join('') : '<div class="sig-none">No mappings yet. Import signals to auto-populate.</div>';
  return `<details id="sigTickerMap" class="sig-disc${unmapped ? ' warn' : ''}"><summary>Ticker Map ${unmapped ? `<span class="c2 yel">${unmapped} unmapped</span>` : `<span class="c2">${entries.length}</span>`} <span class="sig-chev">▾</span></summary><div class="body">
    <div class="sig-tmbar"><button class="sig-pill" data-action="reResolveAllSignals">Auto-Resolve</button><button class="mny-add sig-tmsave" data-action="saveTickerMapping">Save Mappings</button></div>
    <div class="sig-none" style="margin-bottom:8px">Map asset names to price-API symbols (e.g. MAVI.IS). Unmapped names surface first.</div>
    ${rows}
  </div></details>`;
}

export function renderSig() {
  const el = document.getElementById('sigBody');
  if (!el) return;

  // NOTE: updateSignalStatuses() runs from the price-refresh callback, never here
  // (render → save → render loop). Pure presentation only.

  const signals = S.signals || [];
  const allActive = signals.filter(s => s.status !== 'EXPIRED');
  const expired = signals.filter(s => s.status === 'EXPIRED');
  const pos = _sigPositionTickers();

  const cPos = allActive.filter(s => s.ticker && pos.has(normTicker(s.ticker))).length;
  const cTrig = allActive.filter(s => s.status === 'TRIGGERED').length;
  const cWatch = allActive.filter(s => s.status === 'APPROACHING').length;

  let active = allActive;
  if (_sigFilter.status !== 'ALL') active = active.filter(s => s.status === _sigFilter.status);
  if (_sigFilter.action !== 'ALL') active = active.filter(s => s.action === _sigFilter.action);
  if (_sigFilter.source !== 'ALL') active = active.filter(s => s.sourceName === _sigFilter.source);
  if (_sigFilter.productGroup !== 'ALL') active = active.filter(s => signalProductGroup(s) === _sigFilter.productGroup);
  if (_sigFilter.lens === 'positions') active = active.filter(s => s.ticker && pos.has(normTicker(s.ticker)));
  else if (_sigFilter.lens === 'triggering') active = active.filter(s => s.status === 'TRIGGERED');
  else if (_sigFilter.lens === 'watch') active = active.filter(s => s.status === 'APPROACHING');
  const showExpired = _sigFilter.lens === 'expired';

  const uniqueSources = [...new Set(allActive.map(s => s.sourceName).filter(Boolean))];
  const cmap = signalAnalystConfidence();
  let bestAnalyst = null;
  Object.entries(cmap).forEach(([name, v]) => { if (v.resolved && (!bestAnalyst || v.score > bestAnalyst.score)) bestAnalyst = { name, score: v.score, n: v.resolved }; });

  // ── header (canonical .mny-hd shell; signals-green title + spectrum button) ──
  let html = `<div class="mny-hd">
    <span class="mny-live"></span><h1 class="sig-h1">Signals</h1>
    <span class="mny-meta"><b>${cPos} touching positions</b> · ${allActive.length} active · ${cTrig} triggered${bestAnalyst ? ` · best analyst ${bestAnalyst.score}% (n=${bestAnalyst.n})` : ''}</span>
    <div class="sig-tools">
      <button class="sig-ghost" data-action="openPasteSignal">⇪ Import</button>
      <button class="sig-ghost" data-action="fetchSignalsFromMongo">⟳ Mongo</button>
      <button class="sig-ghost" data-action="enrichSignalsAI">✦ AI Enrich</button>
      <button class="mny-add" data-action="openPasteSignal">+ Paste Signal</button>
    </div>
  </div>`;

  // ── lens tabs ──
  const lens = (label, val, n) => `<button class="${_sigFilter.lens === val ? 'on' : ''}" data-action="setSignalFilter" data-args='["lens","${val}"]'>${label} <span class="c">${n}</span></button>`;
  html += `<div class="sig-lens">
    ${lens('Affecting my positions', 'positions', cPos)}
    ${lens('All active', 'ALL', allActive.length)}
    ${lens('Triggered', 'triggering', cTrig)}
    ${lens('Watch', 'watch', cWatch)}
    ${lens('Expired', 'expired', expired.length)}
  </div>`;

  // ── alerts banner ──
  const undismissed = (S.signalAlerts || []).filter(a => a.triggered && !a.dismissedAt);
  if (undismissed.length) {
    html += `<div class="sig-alerts"><div class="sig-alh"><b>⚠ ${undismissed.length} unread alert(s)</b><button class="sig-mini" data-action="dismissAllSignalAlerts">Dismiss all</button></div>`;
    undismissed.slice(0, 5).forEach(a => {
      const s = signals.find(x => x.id === a.signalId); if (!s) return;
      html += `<div class="sig-alrow"><div><span class="tag" style="color:${a.type === 'TRIGGERED' ? 'var(--red)' : 'var(--yel)'}">${esc(a.type)}</span> <b>${esc(s.assetName || '')}</b> <span class="mono">${esc(s.ticker || '')}${a.triggeredAt ? ' · ' + a.triggeredAt.slice(11, 16) : ''}</span></div><button class="sig-mini" data-action="dismissSignalAlert" data-args='["${a.id}"]'>✓</button></div>`;
    });
    html += `</div>`;
  }

  // ── hero ──
  if (!showExpired) html += _sigHeroHTML();

  // ── queue (confidence-ranked) ──
  html += `<div class="sig-lbl">Queue · confidence-ranked${(_sigFilter.lens !== 'ALL' && !showExpired) ? ' · ' + esc(_sigFilter.lens === 'positions' ? 'my positions' : _sigFilter.lens) + ' lens' : ''}</div>`;
  if (_sigFilter.groupBy !== 'none' && !showExpired) {
    active = [...active].sort((a, b) => _sigGroupKey(a).localeCompare(_sigGroupKey(b)) || _sigRank(b, cmap) - _sigRank(a, cmap));
  } else {
    active = [...active].sort((a, b) => _sigRank(b, cmap) - _sigRank(a, cmap));
  }
  if (showExpired) {
    html += expired.length ? expired.map(_sigExpiredRow).join('') : '<div class="sig-empty">No expired signals.</div>';
  } else if (!active.length) {
    html += `<div class="sig-empty">No signals in this lens. Try <b>All active</b>, adjust filters, or import a report.</div>`;
  } else {
    let lastG = null;
    active.forEach(sig => {
      if (_sigFilter.groupBy !== 'none') { const g = _sigGroupKey(sig); if (g !== lastG) { lastG = g; html += `<div class="sig-ghd">${esc(g)}</div>`; } }
      html += _sigQueueRow(sig);
    });
  }

  // ── analytics ──
  if (!showExpired) html += _sigAnalyticsStrip();

  // ── filter/group disclosure (preserves #sig-filter-bar + delegated listeners) ──
  html += `<details class="sig-disc"><summary>Browse, filter &amp; group <span class="sig-chev">▾</span></summary><div class="body">
    ${_sigGroupPills(allActive)}
    ${_sigGroupByControl()}
    <div id="sig-filter-bar" class="sig-fbar">
      <select data-action-change="signalFilterChange" data-field="status" class="sig-sel">
        <option value="ALL"${_sigFilter.status === 'ALL' ? ' selected' : ''}>All status</option>
        <option value="NEW"${_sigFilter.status === 'NEW' ? ' selected' : ''}>New</option>
        <option value="APPROACHING"${_sigFilter.status === 'APPROACHING' ? ' selected' : ''}>Approaching</option>
        <option value="TRIGGERED"${_sigFilter.status === 'TRIGGERED' ? ' selected' : ''}>Triggered</option>
        <option value="ACTED"${_sigFilter.status === 'ACTED' ? ' selected' : ''}>Acted</option>
      </select>
      <select data-action-change="signalFilterChange" data-field="action" class="sig-sel">
        <option value="ALL"${_sigFilter.action === 'ALL' ? ' selected' : ''}>All actions</option>
        ${Object.keys(ACTION_DIRECTION).map(a => `<option value="${a}"${_sigFilter.action === a ? ' selected' : ''}>${a}</option>`).join('')}
      </select>
      ${uniqueSources.length > 1 ? `<select data-action-change="signalFilterChange" data-field="source" class="sig-sel"><option value="ALL"${_sigFilter.source === 'ALL' ? ' selected' : ''}>All sources</option>${uniqueSources.map(s => `<option value="${esc(s)}"${_sigFilter.source === s ? ' selected' : ''}>${esc(s)}</option>`).join('')}</select>` : ''}
      ${(_sigFilter.status !== 'ALL' || _sigFilter.action !== 'ALL' || _sigFilter.source !== 'ALL') ? `<button class="sig-pill" data-action="clearSignalFilters">Clear filters</button>` : ''}
      <span class="sig-fcount">${(active.length !== allActive.length && !showExpired) ? `showing ${active.length} of ${allActive.length}` : `${(S.signalSources || []).length} source(s)`}</span>
    </div>
    ${_sigAnalystPanel()}
  </div></details>`;

  // ── ticker map disclosure ──
  html += _sigTickerMapHTML();

  // ── expired disclosure (when not already in expired lens) ──
  if (expired.length && !showExpired) {
    html += `<details class="sig-disc"><summary>Expired signals <span class="c2">${expired.length}</span> <span class="sig-chev">▾</span></summary><div class="body">${expired.slice(0, 40).map(_sigExpiredRow).join('')}${expired.length > 40 ? `<div class="sig-none">+ ${expired.length - 40} more</div>` : ''}</div></details>`;
  }

  html += `<div class="mny-foot">FORWARD-OBSERVED OUTCOMES · BAYESIAN-SHRUNK (PRIOR 50%, n SHOWN) · CURRENCY-CHECKED TP-SYNC · FEEDS RISK EVENT PANEL</div>`;

  el.innerHTML = html;

  // ⚠ THE FILTER BAR USED TO CARRY ITS OWN LISTENERS AND ITS OWN VOCABULARY. Four names -
  // `filter-status`, `filter-action`, `filter-source`, `clear-filters` - were written as
  // `data-action` and were the ONLY four static action names in the app not in main.js's
  // `_actions`. They worked, because `#sig-filter-bar` attached a local `change` and `click`
  // listener on every render. What they also did, after PROC-003 closed the ancestor
  // fall-through, was make the GLOBAL delegator log `Unregistered action "clear-filters"` to the
  // console on every click - a wiring error printed on a control that was working.
  //
  // They are on the same rails as the other 250 now: `data-action-change` for the selects (the
  // change delegator passes the event, so the field travels in `data-field`) and a registered
  // `clearSignalFilters` for the button. The local listeners are gone with them, and
  // `tests/core/every-data-action-has-a-handler.test.js` fails the build on a fifth. (2026-08-16)
}

// ─── MongoDB canonical_market_states feed (read-only via Netlify function) ───
const API_BASE = '/api';

// evidence_phrases: [{phrase, used_for}] → ["phrase", ...]
function _phrases(ev) {
  if (!Array.isArray(ev)) return [];
  return ev.map(e => (typeof e === 'string' ? e : (e && e.phrase) || '')).filter(Boolean);
}

/**
 * Map ONE canonical_market_states document → array of ZAGX signals (one per asset).
 * Doc shape: { metadata:{id,channel_name,title,url,published_at,channel_id},
 *   created_at, assets:[{asset_name,asset_class,analyst_action,recommended_action,
 *   trend,watch_triggers[],notes[],evidence_phrases[{phrase,used_for}]}], market_context, ... }
 */
function mapMarketStateDoc(doc) {
  const m = doc.metadata || {};
  const vidId = m.id || doc.created_at || uid();
  const sourceName = m.channel_name || 'MongoDB';
  const sourceDate = (m.published_at || doc.created_at || '').slice(0, 10);
  const out = [];
  (doc.assets || []).forEach(a => {
    const name = a.asset_name || '';
    if (!name) return;
    const ticker = (resolveTickerMap(name) || '').toUpperCase();
    const noteArr = [...(a.notes || [])];
    if (a.analyst_action) noteArr.unshift('Analyst action: ' + a.analyst_action);
    // Extract numeric support/target from the analyst's watch_triggers/notes text — same engine as the HTML paste path
    const lv = _extractLevels(a.watch_triggers || [], a.notes || []);
    const supV = lv.support ? lv.support.value : null, supC = (lv.support && lv.support.currency) || '';
    const tgtV = lv.target ? lv.target.value : null, tgtC = (lv.target && lv.target.currency) || '';
    out.push({
      id: uid(),
      _extId: vidId + '::' + name,                 // stable per-asset id → dedupe
      sourceId: 'mongo:' + (m.channel_id || sourceName),
      sourceName,
      sourceTitle: m.title || '',
      sourceUrl: m.url || '',
      sourceDate,
      ticker,
      assetName: name,
      assetClass: a.asset_class || 'OTHER',
      action: a.recommended_action || a.analyst_action || '',  // recommended = actionable
      trend: a.trend || '',
      supportLevel: supV, supportCcy: supC, supportLevelTRY: _normalizeLevelToTRY(supV, supC, ticker),
      targetLevel: tgtV, targetCcy: tgtC, targetLevelTRY: _normalizeLevelToTRY(tgtV, tgtC, ticker),
      watchTriggers: a.watch_triggers || [],
      notes: noteArr,
      // evidencePhrases intentionally NOT stored — display-only rationale, heaviest field (Supabase bloat). See save() prune.
      status: 'NEW',
      createdAt: now(),
      _updatedAt: Date.now(),
    });
  });
  return out;
}

/**
 * Pull canonical_market_states from MongoDB Atlas (via /api/signals-feed) and merge
 * new asset-signals into S.signals. Read-only; dedupes on _extId; registers each
 * channel as a signalSource. Respects the 500-cap in save(). No Supabase bloat beyond signals.
 * @param {Object} [opts] { since:'YYYY-MM-DD', limit:Number }
 */
export async function fetchSignalsFromMongo(opts = {}) {
  const params = new URLSearchParams();
  if (opts.since) params.set('since', opts.since);
  if (opts.limit) params.set('limit', String(opts.limit));
  // db/collection are configurable in Settings (non-secret); the URI stays server-side in Netlify env
  if (S.settings?.mongoDb) params.set('db', S.settings.mongoDb);
  if (S.settings?.mongoColl) params.set('collection', S.settings.mongoColl);
  let res;
  try {
    res = await fetch(`${API_BASE}/signals-feed${params.toString() ? '?' + params : ''}`, {
      signal: AbortSignal.timeout(15000),
    });
  } catch (e) {
    toast('Signals feed unreachable: ' + e.message, 'a-r');
    return { ok: false, imported: 0 };
  }
  if (!res.ok) {
    let msg = 'Signals feed error (' + res.status + ')';
    try { const b = await res.json(); if (b.error) msg = b.error; } catch (_e) { /* ignore */ }
    toast(msg, 'a-r');
    return { ok: false, imported: 0 };
  }
  const data = await res.json();
  const docs = data.signals || data.docs || [];
  if (!S.signals) S.signals = [];
  if (!S.signalSources) S.signalSources = [];
  const byExtId = new Map(S.signals.filter(s => s._extId).map(s => [s._extId, s]));
  let imported = 0, unmapped = 0, backfilled = 0, tooOld = 0;

  // The retention cap keeps the newest MONGO_SIG_CAP mongo signals by report date. Importing
  // rows older than that boundary and then having save() delete them again is not a no-op:
  // measured live on 2026-08-03, one pull imported 366 and the cap dropped 216 — and because
  // the dropped rows leave S.signals, the next pull sees them as new and does it again. The
  // same 216 rows were being written and deleted on every pull, forever, each round marking
  // the state dirty and costing a cloud write. Cap the INPUT at the same boundary instead.
  const _heldMongo = (S.signals || [])
    .filter(x => x && typeof x.sourceId === 'string' && x.sourceId.startsWith('mongo'))
    .map(x => x.sourceDate || x.createdAt || '')
    .filter(Boolean)
    .sort((a, b) => b.localeCompare(a));
  const _cutoff = _heldMongo.length >= MONGO_SIG_CAP ? _heldMongo[MONGO_SIG_CAP - 1] : null;
  // Report dates are DAYS, and a day holds many rows. A row dated exactly the cutoff day is not
  // "inside the window": the cap already keeps the earlier copies of that day and drops the
  // newcomers by stable sort, so admitting it imports it and deletes it on every pull - 10 rows
  // and one cloud write per pull, measured 2026-10-12 within a minute of the feed being
  // reconnected. The boundary day is admitted only when it is also the newest held day, which
  // is the one case where the row belongs to the window and not to its edge.
  const _edgeIsNewest = _cutoff !== null && _cutoff === _heldMongo[0];

  docs.forEach(doc => {
    mapMarketStateDoc(doc).forEach(sig => {
      const existing = byExtId.get(sig._extId);
      const _d = sig.sourceDate || sig.createdAt || '';
      if (!existing && _cutoff && (_d < _cutoff || (_d === _cutoff && !_edgeIsNewest))) { tooOld++; return; }
      if (existing) {
        // backfill numeric levels onto older imports that were stored without them
        if (existing.supportLevel == null && sig.supportLevel != null) {
          existing.supportLevel = sig.supportLevel; existing.supportCcy = sig.supportCcy; existing.supportLevelTRY = sig.supportLevelTRY; backfilled++;
        }
        if (existing.targetLevel == null && sig.targetLevel != null) {
          existing.targetLevel = sig.targetLevel; existing.targetCcy = sig.targetCcy; existing.targetLevelTRY = sig.targetLevelTRY; backfilled++;
        }
        return;
      }
      byExtId.set(sig._extId, sig);
      if (!sig.ticker) unmapped++;
      S.signals.push(sig);
      imported++;
      let src = S.signalSources.find(s => s.id === sig.sourceId);
      if (!src) {
        src = { id: sig.sourceId, name: sig.sourceName, analyst: sig.sourceName, type: 'mongo',
                trustScore: 50, hitCount: 0, missCount: 0, totalSignals: 0, assetClasses: [], _updatedAt: Date.now() };
        S.signalSources.push(src);
      }
      src.totalSignals++;
      if (!src.assetClasses.includes(sig.assetClass)) src.assetClasses.push(sig.assetClass);
    });
  });
  // A pull that returns plenty and imports nothing is a normal outcome, not a failure — but
  // it has to be legible, or "0 new" reads as a broken feed.
  if (tooOld) console.warn('[ZAGX] signal pull: skipped ' + tooOld + ' report(s) older than the retention window (' + _cutoff + ')');

  // BEFORE the save, and saved unconditionally. This was set after it, inside a save guarded
  // on `imported > 0 || backfilled > 0` — so a pull that found nothing new never persisted the
  // fact that it ran, and Tools → Integrity kept reading "no pull recorded yet" forever. The
  // record is the evidence that the integration is alive; a quiet pull is exactly the case
  // that needs it. (audit 2026-08-03)
  S._lastSignalPull = { at: Date.now(), fetched: docs.length, imported, skippedOld: tooOld };
  save();

  // Auto-refresh: resolve tickers (incl. existing via global ref), recompute status vs live prices
  try { reResolveAllSignals(); } catch (_e) { /* optional */ }
  try { updateSignalStatuses(); } catch (_e) { /* optional */ }
  try { renderSig(); } catch (_e) { /* optional */ }
  // …and the two surfaces that READ this pull but were never told it happened: the watchdog
  // check on Tools → Integrity, and the notification bell that mirrors it. The owner watched
  // the pull succeed in the console while both kept showing the pre-pull text until he
  // reloaded by hand. A background job that changes what a check says has to re-render it.
  // renderAll drives the bell itself (render-loop.js), so marking dirty and rendering is all
  // that is needed — no separate bell call to fall out of sync.
  try { if (typeof window !== 'undefined' && window.markDirty) window.markDirty('signals', 'integrity'); } catch (_e) { /* optional */ }
  try { if (typeof window !== 'undefined' && window.renderAll) window.renderAll(); } catch (_e) { /* optional */ }

  toast(`MongoDB: ${imported} new · ${backfilled} level(s) backfilled · ${docs.length} report(s)` + (unmapped ? ` · ${unmapped} need ticker mapping` : ''), (imported || backfilled) ? 'a-g' : 'a-b');
  return { ok: true, imported, backfilled, fetched: docs.length, unmapped };
}
